<template>
    <div class="tamepulse-chat-panel flex min-h-0 flex-col overflow-hidden" :class="panelRootClass">
        <Alert v-if="localError" type="error" :class="alertClass">
            {{ localError }}
        </Alert>

        <Alert v-if="!realtimeAvailable" type="warning" :class="alertClass">
            {{ t('tamepulse.chat.reverbUnavailable') }}
        </Alert>

        <div :class="layoutClass">
            <aside v-if="showConversationSidebar" class="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div class="min-h-0 flex-1 overflow-y-auto p-3">
                    <div
                        v-for="conversation in conversations"
                        :key="conversation.id"
                        class="group mb-2 flex w-full items-start gap-2 rounded-lg border transition"
                        :class="String(conversation.id) === String(selectedConversationId) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'"
                    >
                        <button
                            type="button"
                            class="min-w-0 flex-1 px-3 py-3 text-left"
                            @click="selectConversation(conversation)"
                        >
                            <div class="truncate text-sm font-semibold text-slate-950">{{ conversation.title || t('tamepulse.chat.untitled') }}</div>
                            <div class="mt-2 text-[11px] uppercase tracking-wide text-slate-400">
                                {{ t('tamepulse.chat.lastMessageAt') }} - {{ formatDate(conversation.last_message_at || conversation.updated_at) }}
                            </div>
                        </button>

                        <ModalButton modal-container-class="modal-container !w-[460px] !max-w-[92vw]">
                            <template #buttonContent="{ openModal }">
                                <button
                                    type="button"
                                    class="mr-2 mt-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-slate-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:border-red-300 focus:bg-red-50 focus:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-100"
                                    :aria-label="t('tamepulse.chat.deleteConversation.action')"
                                    :title="t('tamepulse.chat.deleteConversation.action')"
                                    @click.stop="openModal"
                                >
                                    <fa-icon :icon="deletingConversationIds[conversation.id] ? 'spinner' : 'trash'" :class="deletingConversationIds[conversation.id] ? 'animate-spin' : ''" />
                                </button>
                            </template>

                            <template #header>
                                <div class="text-lg font-semibold text-slate-950">{{ t('tamepulse.chat.deleteConversation.title') }}</div>
                            </template>

                            <template #body>
                                <div class="space-y-3 text-sm text-slate-600">
                                    <p>{{ t('tamepulse.chat.deleteConversation.message') }}</p>
                                    <div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-medium text-slate-800">
                                        {{ conversation.title || t('tamepulse.chat.untitled') }}
                                    </div>
                                </div>
                            </template>

                            <template #footer="{ closeModal }">
                                <div class="flex items-center justify-between gap-3">
                                    <Button type="button" @click="closeModal">
                                        <TextWithIcon icon="xmark">{{ t('tamepulse.common.cancel') }}</TextWithIcon>
                                    </Button>

                                    <Button type="button" class="warning" :disabled="deletingConversationIds[conversation.id]" @click="deleteConversation(conversation, closeModal)">
                                        <TextWithIcon :icon="deletingConversationIds[conversation.id] ? 'spinner' : 'trash'">
                                            {{ deletingConversationIds[conversation.id] ? t('tamepulse.chat.deleteConversation.deleting') : t('tamepulse.chat.deleteConversation.confirm') }}
                                        </TextWithIcon>
                                    </Button>
                                </div>
                            </template>
                        </ModalButton>
                    </div>
                </div>
            </aside>

            <section class="flex min-h-0 flex-col" :class="chatSectionClass">
            <header class="flex shrink-0 items-center justify-between gap-3 border-b" :class="headerClass">
                <div class="min-w-0 flex-1">
                    <h2 class="truncate font-semibold text-slate-950" :class="conversationTitleClass">
                        {{ selectedConversation?.title || t('tamepulse.chat.emptyTitle') }}
                    </h2>

                    <select
                        v-if="isCompact && conversations.length"
                        :value="selectedConversationId || ''"
                        class="mt-1.5 w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 shadow-inner shadow-slate-100/60 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                        :aria-label="t('tamepulse.chat.selectConversation')"
                        @change="selectConversationById($event.target.value)"
                    >
                        <option value="">{{ t('tamepulse.chat.newChat') }}</option>
                        <option
                            v-for="conversation in conversations"
                            :key="conversation.id"
                            :value="conversation.id"
                        >
                            {{ conversation.title || t('tamepulse.chat.untitled') }}
                        </option>
                    </select>
                </div>

                <button
                    type="button"
                    class="inline-flex shrink-0 items-center justify-center transition"
                    :class="newChatButtonClass"
                    :aria-label="t('tamepulse.chat.newChat')"
                    :title="t('tamepulse.chat.newChat')"
                    @click="startNewConversation"
                >
                    <fa-icon icon="plus" />
                </button>
            </header>

            <div ref="messagesPane" class="tamepulse-chat-scroll min-h-0 flex-1 basis-0 overflow-y-auto" :class="messagesPaneClass" @scroll="onMessagesScroll">
                <div v-if="!selectedConversation && !draftHasContent" class="mx-auto flex h-full max-w-lg flex-col items-center justify-center text-center">
                    <div class="text-base font-semibold text-slate-950">{{ t('tamepulse.chat.emptyTitle') }}</div>
                    <p class="mt-2 text-sm text-slate-500">{{ t('tamepulse.chat.emptyDescription') }}</p>
                </div>

                <div v-else-if="loadingInitialMessages && !conversationMessages.length" class="mx-auto flex h-full max-w-lg flex-col items-center justify-center text-slate-400">
                    <fa-icon icon="spinner" class="animate-spin" />
                </div>

                <div v-else-if="!conversationMessages.length" class="mx-auto flex h-full max-w-lg flex-col items-center justify-center text-center">
                    <div class="text-base font-semibold text-slate-950">{{ t('tamepulse.chat.noMessages') }}</div>
                </div>

                <div v-else :class="messageListClass">
                    <div v-if="loadingOlderMessages" class="flex justify-center text-slate-400">
                        <fa-icon icon="spinner" class="animate-spin" />
                    </div>

                    <article
                        v-for="message in conversationMessages"
                        :key="message.id"
                        class="flex"
                        :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
                    >
                        <div
                            class="rounded-2xl"
                            :class="messageBubbleClass(message)"
                        >
                            <div class="mb-1 text-xs font-semibold uppercase tracking-wide" :class="message.role === 'user' ? 'text-blue-100' : 'text-slate-400'">
                                {{ message.role === 'user' ? t('tamepulse.chat.you') : t('tamepulse.chat.assistant') }}
                            </div>

                            <div v-if="message.status === 'waiting' && pendingHumanInput(message)" class="space-y-3 text-sm text-slate-700">
                                <div class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-amber-950">
                                    <fa-icon icon="circle-question" class="mt-1 shrink-0 text-amber-600" />
                                    <div class="min-w-0 flex-1">
                                        <div class="font-semibold">{{ t('tamepulse.chat.humanInput.title') }}</div>
                                        <p class="mt-1 whitespace-pre-wrap leading-6">{{ pendingHumanInput(message).question || t('tamepulse.chat.humanInput.defaultQuestion') }}</p>
                                    </div>
                                </div>

                                <form class="space-y-3" @submit.prevent="submitHumanInput(message)">
                                    <div v-if="humanInputChoices(message).length" class="flex flex-wrap gap-2">
                                        <button
                                            v-for="choice in humanInputChoices(message)"
                                            :key="choice.id"
                                            type="button"
                                            class="rounded-md border px-3 py-2 text-left text-xs font-semibold transition"
                                            :class="humanInputChoiceButtonClass(message, choice.id)"
                                            @click="selectHumanInputChoice(message, choice.id)"
                                        >
                                            <span class="block">{{ choice.label }}</span>
                                            <span v-if="choice.description" class="mt-0.5 block text-[11px] font-medium opacity-75">{{ choice.description }}</span>
                                        </button>

                                        <button
                                            v-if="humanInputAllowsOther(message)"
                                            type="button"
                                            class="rounded-md border px-3 py-2 text-xs font-semibold transition"
                                            :class="humanInputChoiceButtonClass(message, HUMAN_OTHER_CHOICE_ID)"
                                            @click="selectHumanInputChoice(message, HUMAN_OTHER_CHOICE_ID)"
                                        >
                                            {{ t('tamepulse.chat.humanInput.other') }}
                                        </button>
                                    </div>

                                    <label v-if="humanInputTextVisible(message)" class="block">
                                        <Label>{{ t('tamepulse.chat.humanInput.answer') }}</Label>
                                        <textarea
                                            :value="humanInputDraft(message).text"
                                            rows="3"
                                            class="block w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                            :placeholder="t('tamepulse.chat.humanInput.answerPlaceholder')"
                                            @input="updateHumanInputText(message, $event.target.value)"
                                        ></textarea>
                                    </label>

                                    <p v-if="humanInputDraft(message).error" class="text-sm text-red-600">
                                        {{ humanInputDraft(message).error }}
                                    </p>

                                    <div class="flex justify-end">
                                        <Button type="submit" :disabled="submittingHumanInput[message.id]">
                                            <TextWithIcon :icon="submittingHumanInput[message.id] ? 'spinner' : 'paper-plane'">
                                                {{ submittingHumanInput[message.id] ? t('tamepulse.chat.humanInput.submitting') : t('tamepulse.chat.humanInput.submit') }}
                                            </TextWithIcon>
                                        </Button>
                                    </div>
                                </form>
                            </div>

                            <div v-else-if="message.status === 'waiting'" class="flex items-center gap-2 text-sm text-amber-700">
                                <fa-icon icon="circle-question" class="shrink-0 text-amber-500" />
                                <span>{{ t('tamepulse.chat.humanInput.defaultQuestion') }}</span>
                            </div>

                            <div v-else-if="message.status === 'pending'" class="space-y-2 text-sm text-slate-500">
                                <div class="flex items-center gap-2">
                                    <fa-icon icon="spinner" class="shrink-0 animate-spin text-slate-400" />
                                    <span>{{ t('tamepulse.chat.pending') }}</span>
                                </div>

                                <div v-if="chatProgressSteps(message).length" class="space-y-1 border-l border-slate-200 pl-3">
                                    <div
                                        v-for="step in chatProgressSteps(message)"
                                        :key="step.key"
                                        class="flex items-start gap-2 text-xs leading-5"
                                    >
                                        <fa-icon
                                            :icon="chatProgressIcon(step)"
                                            class="mt-1 shrink-0"
                                            :class="chatProgressIconClass(step)"
                                        />
                                        <span>{{ chatProgressLabel(step) }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="message.status === 'failed'" class="space-y-2">
                                <div class="text-sm font-semibold">{{ t('tamepulse.chat.failed') }}</div>
                                <p class="whitespace-pre-wrap text-sm">{{ message.error }}</p>
                            </div>

                            <div v-else-if="message.status === 'cancelled'" class="flex items-center gap-2 text-sm text-slate-500">
                                <fa-icon icon="ban" class="shrink-0 text-slate-400" />
                                <span>{{ t('tamepulse.chat.cancelled') }}</span>
                            </div>

                            <p v-else-if="message.role === 'user' && message.content" class="whitespace-pre-wrap text-sm leading-6">{{ message.content }}</p>

                            <div v-else-if="message.content">
                                <template
                                    v-for="part in mailMessageContentParts(message)"
                                    :key="part.key"
                                >
                                    <MdPreview
                                        v-if="part.content"
                                        :model-value="part.content"
                                        language="en-US"
                                        preview-theme="github"
                                        class="tamepulse-chat-markdown-preview"
                                    />

                                    <div v-if="part.actions.length" class="my-3 flex flex-wrap gap-2">
                                        <button
                                            v-for="action in part.actions"
                                            :key="action.key"
                                            type="button"
                                            class="inline-flex max-w-full items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-indigo-950/10 transition hover:bg-indigo-500"
                                            @mousedown.prevent
                                            @keydown.enter.prevent.stop="handleAssistantActionEnter"
                                            @keyup.enter.prevent.stop
                                            @click="openMailMessage(action)"
                                        >
                                            <fa-icon icon="envelope" class="shrink-0" />
                                            <span class="truncate">{{ action.label }}</span>
                                        </button>
                                    </div>
                                </template>
                            </div>

                            <div v-if="knowledgeSources(message).length" class="mt-3 border-t border-slate-100 pt-3">
                                <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{{ t('tamepulse.chat.sources') }}</div>
                                <div class="flex flex-wrap gap-x-4 gap-y-2">
                                    <RouterLink
                                        v-for="source in knowledgeSources(message)"
                                        :key="source.key"
                                        :to="knowledgeSourceRoute(source)"
                                        class="inline-flex max-w-full items-center gap-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-500"
                                    >
                                        <fa-icon icon="file-lines" class="shrink-0 text-slate-400" />
                                        <span class="truncate">{{ source.label }}</span>
                                    </RouterLink>
                                </div>
                            </div>

                            <div v-if="visibleMailDraftActions(message).length" class="mt-3 border-t border-slate-100 pt-3">
                                <div class="flex flex-wrap gap-2">
                                    <button
                                        v-for="action in visibleMailDraftActions(message)"
                                        :key="action.key"
                                        type="button"
                                        class="inline-flex max-w-full items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-indigo-950/10 transition hover:bg-indigo-500"
                                        @mousedown.prevent
                                        @keydown.enter.prevent.stop="handleAssistantActionEnter"
                                        @keyup.enter.prevent.stop
                                        @click="openMailDraft(action)"
                                    >
                                        <fa-icon icon="envelope" class="shrink-0" />
                                        <span class="truncate">{{ action.label }}</span>
                                    </button>
                                </div>
                            </div>

                            <div v-if="visibleMailMessageActions(message).length" class="mt-3 border-t border-slate-100 pt-3">
                                <div class="flex flex-wrap gap-2">
                                    <button
                                        v-for="action in visibleMailMessageActions(message)"
                                        :key="action.key"
                                        type="button"
                                        class="inline-flex max-w-full items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-indigo-950/10 transition hover:bg-indigo-500"
                                        @mousedown.prevent
                                        @keydown.enter.prevent.stop="handleAssistantActionEnter"
                                        @keyup.enter.prevent.stop
                                        @click="openMailMessage(action)"
                                    >
                                        <fa-icon icon="envelope" class="shrink-0" />
                                        <span class="truncate">{{ action.label }}</span>
                                    </button>
                                </div>
                            </div>

                            <div v-if="visibleQuoteEntityActions(message).length" class="mt-3 border-t border-slate-100 pt-3">
                                <div class="flex flex-wrap gap-2">
                                    <button
                                        v-for="action in visibleQuoteEntityActions(message)"
                                        :key="action.key"
                                        type="button"
                                        class="inline-flex max-w-full items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-indigo-950/10 transition hover:bg-indigo-500"
                                        @mousedown.prevent
                                        @keydown.enter.prevent.stop="handleAssistantActionEnter"
                                        @keyup.enter.prevent.stop
                                        @click="openQuoteEntity(action)"
                                    >
                                        <fa-icon :icon="quoteEntityActionIcon(action)" class="shrink-0" />
                                        <span class="truncate">{{ action.label }}</span>
                                    </button>
                                </div>
                            </div>

                            <div v-if="message.attachments?.length" class="mt-3 space-y-2">
                                <div
                                    v-for="(attachment, index) in visibleAttachments(message.attachments)"
                                    :key="`${attachment.name}-${index}`"
                                    class="rounded-lg px-3 py-2 text-xs"
                                    :class="message.role === 'user' ? 'bg-blue-500 text-blue-50' : 'bg-slate-100 text-slate-600'"
                                >
                                    <div class="flex items-center gap-2 font-medium">
                                        <fa-icon icon="paperclip" />
                                        <span class="truncate">{{ attachment.name }}</span>
                                    </div>
                                    <div class="mt-1 opacity-80">
                                        {{ formatBytes(attachment.size) }}
                                        <span v-if="!attachment.included_in_prompt"> - {{ t('tamepulse.chat.fileNotReadable') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </div>

            <form class="shrink-0 border-t bg-white" :class="composerClass" @submit.prevent="handleComposerSubmit">
                <div class="mb-3 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        class="ml-auto flex min-w-0 max-w-[min(32rem,100%)] items-center gap-2 text-left text-xs font-medium text-slate-500 transition hover:text-indigo-700"
                        @click="openDetailsDrawer('context')"
                    >
                        <span class="min-w-0 truncate">{{ chatAdvancedSummary }}</span>
                        <span
                            v-if="savingRuntimeSettings"
                            class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-slate-400"
                            :aria-label="t('tamepulse.chat.advanced.savingSettings')"
                            :title="t('tamepulse.chat.advanced.savingSettings')"
                        >
                            <fa-icon icon="spinner" class="animate-spin text-[11px]" />
                        </span>
                    </button>

                    <button
                        type="button"
                        class="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm shadow-slate-950/5 transition hover:border-blue-300 hover:text-blue-700"
                        @click="openDetailsDrawer('context')"
                    >
                        <fa-icon icon="chart-simple" />
                        <span>{{ t('tamepulse.chat.details.button') }}</span>
                    </button>

                    <button
                        type="button"
                        class="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm shadow-slate-950/5 transition hover:border-blue-300 hover:text-blue-700"
                        @click="advancedPanelOpen = !advancedPanelOpen"
                    >
                        <fa-icon icon="sliders" />
                        <span>{{ advancedPanelOpen ? t('tamepulse.chat.advanced.hide') : t('tamepulse.chat.advanced.show') }}</span>
                    </button>
                </div>

                <div
                    v-if="advancedPanelOpen"
                    class="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
                    :class="isCompact ? 'max-h-64 overflow-y-auto' : ''"
                >
                    <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <label class="block">
                            <Label>{{ t('tamepulse.chat.advanced.routingMode') }}</Label>
                            <select
                                v-model="chatRoutingMode"
                                class="block h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            >
                                <option
                                    v-for="option in chatRoutingModeOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </label>

                        <label v-if="chatRoutingMode === 'profile'" class="block">
                            <Label>{{ t('tamepulse.chat.advanced.profile') }}</Label>
                            <select
                                v-model="selectedChatProfileId"
                                class="block h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            >
                                <option value="">{{ t('tamepulse.chat.advanced.selectProfile') }}</option>
                                <option
                                    v-for="profile in chatAgentProfiles"
                                    :key="profile.id"
                                    :value="profile.id"
                                >
                                    {{ profile.name }}
                                </option>
                            </select>
                        </label>

                        <label v-if="chatRoutingMode === 'agent'" class="block">
                            <Label>{{ t('tamepulse.chat.advanced.agent') }}</Label>
                            <select
                                v-model="selectedChatAgentId"
                                class="block h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            >
                                <option value="">{{ t('tamepulse.chat.advanced.selectAgent') }}</option>
                                <option
                                    v-for="agent in chatAgents"
                                    :key="agent.id"
                                    :value="agent.id"
                                >
                                    {{ agent.name }}
                                </option>
                            </select>
                        </label>

                        <label class="block">
                            <Label>{{ t('tamepulse.chat.advanced.serviceTier') }}</Label>
                            <select
                                v-model="chatServiceTier"
                                class="block h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            >
                                <option
                                    v-for="option in chatServiceTierOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </label>

                        <div class="block md:col-span-2 xl:col-span-2">
                            <div class="flex flex-wrap items-center justify-between gap-3">
                                <label class="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                                    <input
                                        v-model="chatTemperatureOverride"
                                        type="checkbox"
                                        class="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    >
                                    <span>{{ t('tamepulse.chat.advanced.useCustomTemperature') }}</span>
                                </label>
                                <span class="text-sm font-semibold tabular-nums text-slate-900">{{ chatTemperatureLabel }}</span>
                            </div>

                            <input
                                v-model.number="chatTemperature"
                                type="range"
                                min="0"
                                max="2"
                                step="0.1"
                                class="mt-3 h-2 w-full accent-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="!chatTemperatureOverride"
                                :aria-label="t('tamepulse.chat.advanced.temperature')"
                            >
                        </div>
                    </div>

                    <div class="mt-3 flex flex-wrap items-center gap-4">
                        <label class="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                            <input
                                v-model="chatReadOnly"
                                type="checkbox"
                                class="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            >
                            <span>{{ t('tamepulse.chat.advanced.analysisOnly') }}</span>
                        </label>

                        <label class="inline-flex items-center gap-2 text-sm font-medium text-slate-700" :class="chatReadOnly ? 'opacity-50' : ''">
                            <input
                                v-model="chatAllowLocalShell"
                                type="checkbox"
                                class="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                :disabled="chatReadOnly"
                            >
                            <span>{{ t('tamepulse.chat.advanced.allowLocalShell') }}</span>
                        </label>
                    </div>
                </div>

                <div
                    v-if="liveChatStatus"
                    class="mb-3 flex max-w-full items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium"
                    :class="liveChatStatusClass"
                >
                    <fa-icon
                        :icon="liveChatStatus.icon"
                        class="shrink-0"
                        :class="liveChatStatus.spinning ? 'animate-spin' : ''"
                    />
                    <span class="min-w-0 truncate">{{ liveChatStatus.label }}</span>
                </div>

                <div v-if="selectedFiles.length" class="mb-3 flex flex-wrap gap-2">
                    <span
                        v-for="(file, index) in selectedFiles"
                        :key="`${file.name}-${index}`"
                        class="inline-flex items-center gap-2 rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                    >
                        <fa-icon icon="paperclip" />
                        {{ file.name }}
                        <button type="button" class="text-slate-400 hover:text-red-600" :aria-label="t('tamepulse.chat.removeFile')" @click="removeFile(index)">
                            <fa-icon icon="xmark" />
                        </button>
                    </span>
                </div>

                <div class="flex items-end" :class="isCompact ? 'gap-2' : 'gap-3'">
                    <label class="flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-blue-300 hover:bg-slate-50 hover:text-blue-600" :class="composerButtonClass" :title="t('tamepulse.chat.attachFiles')">
                        <fa-icon icon="paperclip" />
                        <input ref="fileInput" type="file" class="sr-only" multiple @change="onFilesSelected" />
                    </label>

                    <textarea
                        ref="messageInput"
                        v-model="messageText"
                        rows="1"
                        class="max-h-40 flex-1 resize-none rounded-lg border border-slate-300 px-3 text-sm leading-6 text-slate-900 shadow-sm shadow-slate-100/80 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        :class="composerInputClass"
                        :placeholder="t('tamepulse.chat.messagePlaceholder')"
                        @keydown.enter.exact.prevent="handleComposerSubmit"
                    />

                    <Button v-if="!isCompact" type="submit" :disabled="composerActionDisabled" :class="composerActionIsCancel ? 'warning' : ''">
                        <TextWithIcon :icon="composerActionIsCancel ? 'stop' : 'paper-plane'">{{ composerActionLabel }}</TextWithIcon>
                    </Button>

                    <button
                        v-else
                        type="submit"
                        class="flex shrink-0 items-center justify-center rounded-lg text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                        :class="[composerButtonClass, composerActionIsCancel ? 'bg-red-600 shadow-red-900/20 hover:bg-red-500' : 'bg-blue-600 shadow-blue-900/20 hover:bg-blue-500']"
                        :disabled="composerActionDisabled"
                        :aria-label="composerActionLabel"
                        :title="composerActionLabel"
                    >
                        <fa-icon :icon="composerActionIsCancel ? 'stop' : 'paper-plane'" />
                    </button>
                </div>

                <div v-if="contextUsageInlineLabel" class="mt-2 flex" :class="composerUsageInlineOffsetClass">
                    <button
                        type="button"
                        class="inline-flex min-w-0 items-center gap-1.5 truncate text-xs font-semibold transition hover:text-indigo-700"
                        :class="contextUsageInlineClass"
                        @click="openDetailsDrawer('context')"
                    >
                        <fa-icon icon="chart-simple" class="shrink-0" />
                        <span class="truncate">{{ contextUsageInlineLabel }}</span>
                    </button>
                </div>
            </form>
            </section>
        </div>

        <SideDrawer
            v-model="detailsDrawerOpen"
            :title="t('tamepulse.chat.details.title')"
            :close-label="t('tamepulse.common.close')"
        >
            <template #subtitle>
                {{ detailsDrawerSubtitle }}
            </template>

            <div class="space-y-5">
                <div class="grid grid-cols-2 rounded-lg border border-slate-200 bg-slate-50 p-1 text-sm font-semibold">
                    <button
                        type="button"
                        class="rounded-md px-3 py-2 transition"
                        :class="detailsTabClass('context')"
                        @click="detailsDrawerTab = 'context'"
                    >
                        {{ t('tamepulse.chat.details.tabs.context') }}
                    </button>
                    <button
                        type="button"
                        class="rounded-md px-3 py-2 transition"
                        :class="detailsTabClass('activity')"
                        @click="detailsDrawerTab = 'activity'"
                    >
                        {{ t('tamepulse.chat.details.tabs.activity') }}
                    </button>
                </div>

                <template v-if="detailsDrawerTab === 'context'">
                    <div class="rounded-lg border border-slate-200 bg-white px-4 py-3">
                        <div class="flex items-center justify-between gap-3">
                            <div>
                                <div class="text-sm font-semibold text-indigo-600">
                                    {{ t('tamepulse.chat.usage.title') }}
                                </div>
                                <div class="mt-1 text-xs text-slate-500">{{ usageSourceLabel }}</div>
                            </div>
                            <div class="text-sm font-semibold text-slate-900">{{ usagePercentLabel }}</div>
                        </div>

                        <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div class="h-full rounded-full transition-all" :class="usageBarClass" :style="usageBarStyle"></div>
                        </div>

                        <div class="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                            <div
                                v-for="item in usageStats"
                                :key="item.key"
                                class="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                            >
                                <div class="text-xs font-semibold text-slate-500">{{ item.label }}</div>
                                <div class="mt-1 font-medium text-slate-800">{{ item.value }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <Button type="button" @click="resetChatContextSnapshot">
                            <TextWithIcon icon="rotate-left">{{ t('tamepulse.chat.context.actions.reset') }}</TextWithIcon>
                        </Button>
                    </div>

                    <div class="rounded-lg border border-slate-200 bg-white p-4">
                        <div class="flex items-center justify-between gap-3">
                            <div>
                                <div class="text-sm font-semibold text-indigo-600">
                                    {{ t('tamepulse.chat.context.drawer.summaryTitle') }}
                                </div>
                                <div class="mt-1 text-sm text-slate-500">{{ contextSourceLabel }}</div>
                            </div>
                            <div class="text-lg font-semibold text-slate-950">{{ formatTokenCount(chatContextSnapshot.contextWindow || chatUsage.contextWindow) }}</div>
                        </div>

                        <div class="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div class="h-full rounded-full transition-all" :class="usageBarClass" :style="usageBarStyle"></div>
                        </div>

                        <dl class="mt-4 grid gap-3 sm:grid-cols-2">
                            <div
                                v-for="item in contextStats"
                                :key="item.key"
                                class="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                            >
                                <dt class="text-xs font-semibold text-slate-500">{{ item.label }}</dt>
                                <dd class="mt-1 text-sm font-semibold text-slate-900">{{ item.value }}</dd>
                            </div>
                        </dl>
                    </div>

                    <div class="rounded-lg border border-slate-200 bg-white p-4">
                        <div class="text-sm font-semibold text-indigo-600">
                            {{ t('tamepulse.chat.context.drawer.messagesTitle') }}
                        </div>

                        <div v-if="contextItems.length" class="mt-4 space-y-3">
                            <div
                                v-for="item in contextItems"
                                :key="contextItemKey(item)"
                                class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                                <div class="flex items-center justify-between gap-3">
                                    <div class="min-w-0">
                                        <div class="text-sm font-semibold text-slate-900">{{ contextRoleLabel(item.role) }}</div>
                                        <div class="mt-0.5 truncate text-xs text-slate-500">
                                            {{ contextItemMeta(item) }}
                                        </div>
                                    </div>
                                    <div v-if="item.content_truncated || chatContextSnapshot.truncated" class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                                        {{ t('tamepulse.chat.context.truncated') }}
                                    </div>
                                </div>

                                <pre v-if="item.content" class="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs leading-5 text-slate-700">{{ item.content }}</pre>

                                <div v-if="item.tool_calls?.length" class="mt-2 space-y-2">
                                    <div
                                        v-for="toolCall in item.tool_calls"
                                        :key="toolCall.id || toolCall.name"
                                        class="rounded border border-slate-200 bg-white p-2 text-xs text-slate-700"
                                    >
                                        <div class="font-semibold text-slate-900">
                                            {{ t('tamepulse.chat.context.toolCall') }}: {{ toolCall.name }}
                                        </div>
                                        <pre v-if="toolCall.args" class="mt-1 max-h-40 overflow-auto whitespace-pre-wrap leading-5">{{ toolCall.args }}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p v-else class="mt-4 text-sm text-slate-500">
                            {{ t('tamepulse.chat.context.drawer.empty') }}
                        </p>
                    </div>
                </template>

                <template v-else-if="detailsDrawerTab === 'activity'">
                    <div v-if="runnerExecutionDetails" class="rounded-lg border border-indigo-100 bg-indigo-50/70 p-4">
                        <div class="flex items-center justify-between gap-3">
                            <div class="min-w-0">
                                <div class="text-sm font-semibold text-indigo-700">
                                    {{ t('tamepulse.chat.runnerDetails.title') }}
                                </div>
                            </div>
                            <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" :class="runnerExecutionStatusClass">
                                {{ runnerExecutionDetails.statusLabel }}
                            </span>
                        </div>

                        <dl class="mt-3 grid gap-2 text-sm">
                            <div v-if="runnerExecutionDetails.agentName" class="flex items-center justify-between gap-3 rounded-md bg-white/80 px-3 py-2">
                                <dt class="text-slate-500">{{ t('tamepulse.chat.runnerDetails.agent') }}</dt>
                                <dd class="min-w-0 truncate font-medium text-slate-900">{{ runnerExecutionDetails.agentName }}</dd>
                            </div>
                            <div class="flex items-center justify-between gap-3 rounded-md bg-white/80 px-3 py-2">
                                <dt class="text-slate-500">{{ t('tamepulse.chat.runnerDetails.runner') }}</dt>
                                <dd class="min-w-0 truncate font-medium text-slate-900">{{ runnerExecutionDetails.runnerName }}</dd>
                            </div>
                        </dl>
                    </div>

                    <div class="rounded-lg border border-slate-200 bg-white p-4">
                        <div class="mb-3 flex items-center justify-between gap-3">
                            <div class="text-sm font-semibold text-indigo-600">
                                {{ t('tamepulse.chat.events.title') }}
                            </div>
                            <span class="text-xs font-medium text-slate-500">
                                {{ t('tamepulse.chat.events.count', { count: runtimeEvents.length }) }}
                            </span>
                        </div>

                        <div v-if="runtimeEvents.length" class="space-y-2">
                            <div
                                v-for="event in runtimeEvents"
                                :key="event.key"
                                class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                            >
                                <div class="flex items-center justify-between gap-3">
                                    <div class="flex min-w-0 items-center gap-2 font-semibold text-slate-800">
                                        <fa-icon :icon="runtimeEventIcon(event)" :class="runtimeEventIconClass(event)" />
                                        <span class="truncate">{{ runtimeEventLabel(event) }}</span>
                                    </div>
                                    <div class="shrink-0 text-xs text-slate-400">{{ runtimeEventTime(event) }}</div>
                                </div>
                                <details v-if="runtimeEventDetails(event)" class="group mt-2 overflow-hidden rounded-md border border-slate-200 bg-white">
                                    <summary class="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-xs font-semibold text-slate-500 transition hover:text-slate-800 [&::-webkit-details-marker]:hidden">
                                        <span>{{ t('tamepulse.common.details') }}</span>
                                        <fa-icon icon="chevron-down" class="text-[10px] text-slate-400 transition group-open:rotate-180" />
                                    </summary>
                                    <pre class="max-h-40 overflow-auto whitespace-pre-wrap border-t border-slate-100 p-2 text-xs leading-5 text-slate-600">{{ runtimeEventDetails(event) }}</pre>
                                </details>
                            </div>
                        </div>

                        <p v-else class="text-sm text-slate-500">
                            {{ t('tamepulse.chat.events.empty') }}
                        </p>
                    </div>
                </template>
            </div>
        </SideDrawer>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { MdPreview } from 'md-editor-v3'
import { useApolloClient } from '@vue/apollo-composable'
import ModalButton from '@/components/admin/ModalButton.vue'
import SideDrawer from '@/components/admin/SideDrawer.vue'
import { useGlobals, useMutation, useQuery } from '@/composables/useGlobals'
import { useTamepulseChatWidget } from '@/composables/useTamepulseChatWidget'
import {
    applyQuoteEntityFormPatch,
    setPendingQuoteEntityForm,
} from '@/utils/tamepulseQuoteEntityPrefill'
import 'md-editor-v3/lib/preview.css'

const { gql, route, router } = useGlobals()
const { t, locale } = useI18n()
const { client: apolloClient } = useApolloClient()
const {
    mode: chatWidgetMode,
    openPanel: collapseChatWidgetToPanel,
} = useTamepulseChatWidget()

const MESSAGE_PAGE_SIZE = 30
const MAIL_DRAFT_STORAGE_KEY = 'tamepulse.mail.pendingDraft'
const HUMAN_OTHER_CHOICE_ID = '__other'
const QUOTE_ACCOUNT_EDITABLE_FIELDS = [
    'name',
    'account_type',
    'vat_number',
    'fiscal_code',
    'email',
    'phone',
    'notes',
    'request_source',
    'commercial_status',
    'person_ids',
]
const QUOTE_PERSON_EDITABLE_FIELDS = [
    'first_name',
    'last_name',
    'display_name',
    'email',
    'phone',
    'locale',
    'notes',
    'account_ids',
    'customer_ids',
]

const props = defineProps({
    variant: {
        type: String,
        default: 'page',
        validator: (value) => ['page', 'compact', 'expanded'].includes(value),
    },
})

const selectedConversationId = ref(null)
const messageText = ref('')
const selectedFiles = ref([])
const localError = ref('')
const sending = ref(false)
const cancellingRun = ref(false)
const chatChannelName = ref(null)
const realtimeAvailable = ref(Boolean(window.Echo))
const messagesPane = ref(null)
const messageInput = ref(null)
const fileInput = ref(null)
const conversationMessagesById = ref({})
const conversationHasMoreMessages = ref({})
const loadingInitialMessages = ref(false)
const loadingOlderMessages = ref(false)
const awaitingAutoMailDraftConversationId = ref(null)
const awaitingAutoQuoteEntityConversationId = ref(null)
const pendingAutoQuoteEntityPatchRequest = ref(null)
const processedMailDraftActionKeys = ref({})
const processedQuoteEntityActionKeys = ref({})
const advancedPanelOpen = ref(false)
const detailsDrawerOpen = ref(false)
const detailsDrawerTab = ref('context')
const contextSnapshotResetSignature = ref('')
const chatRoutingMode = ref('model')
const selectedChatProfileId = ref('')
const selectedChatAgentId = ref('')
const chatReadOnly = ref(true)
const chatAllowLocalShell = ref(false)
const chatTemperatureOverride = ref(false)
const chatTemperature = ref(0)
const chatServiceTier = ref('')
const runtimeSettingsSavingRequests = ref(0)
const humanInputDrafts = ref({})
const submittingHumanInput = ref({})
const deletingConversationIds = ref({})
let applyingChatRuntimeSettings = false
let runtimeSettingsSaveTimeout = null

const chatConversationsQuery = gql`
    query TamepulseChatIndex {
        tamepulseChatConversations {
            id
            site_id
            connection_id
            environment_id
            title
            status
            runtime_settings
            last_message_at
            created_at
            updated_at
        }
    }
`

const { result } = useQuery(chatConversationsQuery, {}, {
    fetchPolicy: 'cache-and-network',
    context: { suppressLoading: true },
})

const { result: chatRoutingOptionsResult } = useQuery(gql`
    query TamepulseChatRoutingOptions {
        agentProfiles: tamepulseAgentProfiles(agent_type: "coding", limit: 200) {
            id
            name
            description
        }
        agents: tamepulseAgents(limit: 200) {
            id
            key
            name
            description
        }
    }
`, null, {
    fetchPolicy: 'cache-and-network',
    context: { suppressLoading: true },
})

const chatMessagesQuery = gql`
    query TamepulseChatMessages($conversation_id: ID!, $before_id: ID, $limit: Int) {
        tamepulseChatMessages(conversation_id: $conversation_id, before_id: $before_id, limit: $limit) {
            has_more
            messages {
                id
                conversation_id
                role
                status
                content
                error
                attachments
                metadata
                created_at
                updated_at
            }
        }
    }
`

const { mutate: sendMessageMutation } = useMutation(gql`
    mutation TamepulseChatSendMessage($conversation_id: ID, $content: String, $files: [Upload!], $context: Json) {
        tamepulseChatSendMessage(conversation_id: $conversation_id, content: $content, files: $files, context: $context) {
            id
            site_id
            connection_id
            environment_id
            title
            status
            runtime_settings
            last_message_at
            created_at
            updated_at
        }
    }
`, {
    context: { suppressLoading: true },
})

const { mutate: cancelRunMutation } = useMutation(gql`
    mutation TamepulseChatCancelRun($assistant_message_id: ID!) {
        tamepulseChatCancelRun(assistant_message_id: $assistant_message_id) {
            id
            conversation_id
            role
            status
            content
            error
            attachments
            metadata
            created_at
            updated_at
        }
    }
`, {
    context: { suppressLoading: true },
})

const { mutate: submitHumanInputMutation } = useMutation(gql`
    mutation TamepulseChatSubmitHumanInput($assistant_message_id: ID!, $input: Json!) {
        tamepulseChatSubmitHumanInput(assistant_message_id: $assistant_message_id, input: $input) {
            id
            conversation_id
            role
            status
            content
            error
            attachments
            metadata
            created_at
            updated_at
        }
    }
`, {
    context: { suppressLoading: true },
})

const { mutate: updateRuntimeSettingsMutation } = useMutation(gql`
    mutation TamepulseChatUpdateRuntimeSettings($conversation_id: ID!, $runtime_settings: Json!) {
        tamepulseChatUpdateRuntimeSettings(conversation_id: $conversation_id, runtime_settings: $runtime_settings) {
            id
            site_id
            connection_id
            environment_id
            title
            status
            runtime_settings
            last_message_at
            created_at
            updated_at
        }
    }
`, {
    context: { suppressLoading: true },
})

const { mutate: deleteConversationMutation } = useMutation(gql`
    mutation TamepulseChatDeleteConversation($id: ID!) {
        tamepulseChatDeleteConversation(id: $id)
    }
`, {
    context: { suppressLoading: true },
})

const conversations = computed(() => result.value?.tamepulseChatConversations ?? [])
const chatAgentProfiles = computed(() => chatRoutingOptionsResult.value?.agentProfiles ?? [])
const chatAgents = computed(() => chatRoutingOptionsResult.value?.agents ?? [])
const conversationKey = (conversationId) => conversationId ? String(conversationId) : ''
const isCompact = computed(() => props.variant === 'compact')
const showConversationSidebar = computed(() => props.variant !== 'compact')
const panelRootClass = computed(() => props.variant === 'page' ? 'tamepulse-chat-panel-page' : 'h-full')
const alertClass = computed(() => props.variant === 'page' ? 'mt-4' : 'mb-2')
const layoutClass = computed(() => [
    'grid min-h-0 flex-1 overflow-hidden gap-4',
    props.variant === 'page' ? 'mt-4 lg:grid-cols-[320px_minmax(0,1fr)]' : '',
    props.variant === 'compact' ? 'h-full grid-cols-1' : '',
    props.variant === 'expanded' ? 'h-full grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]' : '',
])
const chatSectionClass = computed(() => isCompact.value ? 'overflow-hidden rounded-none border-0 bg-white' : 'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm shadow-slate-950/5')
const headerClass = computed(() => isCompact.value ? 'border-slate-100 bg-white px-3 py-2.5' : 'border-slate-100 bg-white px-5 py-4')
const conversationTitleClass = computed(() => isCompact.value ? 'text-sm leading-5' : 'text-base')
const newChatButtonClass = computed(() => isCompact.value
    ? 'h-8 w-8 rounded-md border border-slate-200 bg-slate-50 text-slate-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600'
    : 'h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600')
const messagesPaneClass = computed(() => isCompact.value ? 'bg-slate-50/80 px-3 py-3' : 'bg-slate-50/80 px-5 py-6')
const messageListClass = computed(() => isCompact.value ? 'space-y-4' : 'space-y-5')
const composerClass = computed(() => isCompact.value ? 'border-slate-100 p-3' : 'border-slate-100 p-4')
const composerButtonClass = computed(() => isCompact.value ? 'h-10 w-10' : 'h-11 w-11')
const composerInputClass = computed(() => isCompact.value ? 'min-h-10 py-1.5' : 'min-h-11 py-2')
const composerUsageInlineOffsetClass = computed(() => isCompact.value ? 'pl-12 pr-12' : 'pl-14 pr-24')

const selectedConversation = computed(() => {
    return conversations.value.find((conversation) => String(conversation.id) === String(selectedConversationId.value)) ?? null
})

const conversationMessages = computed(() => conversationMessagesById.value[conversationKey(selectedConversationId.value)] ?? [])
const hasOlderMessages = computed(() => Boolean(conversationHasMoreMessages.value[conversationKey(selectedConversationId.value)]))
const draftHasContent = computed(() => messageText.value.trim() !== '' || selectedFiles.value.length > 0)
const pendingAssistantMessage = computed(() => {
    return [...conversationMessages.value]
        .reverse()
        .find((message) => message?.role === 'assistant' && ['pending', 'waiting'].includes(message?.status)) ?? null
})
const composerActionIsCancel = computed(() => Boolean(pendingAssistantMessage.value))
const composerActionLabel = computed(() => composerActionIsCancel.value ? t('tamepulse.chat.cancelRun') : t('tamepulse.chat.send'))
const composerActionDisabled = computed(() => composerActionIsCancel.value ? cancellingRun.value : sending.value)
const chatRoutingModeOptions = computed(() => ['model', 'auto', 'profile', 'agent'].map((value) => ({
    value,
    label: t(`tamepulse.chat.advanced.modes.${value}`),
})))
const chatServiceTierOptions = computed(() => ['', 'default', 'fast'].map((value) => ({
    value,
    label: value
        ? t(`tamepulse.chat.advanced.serviceTiers.${value}`)
        : t('tamepulse.chat.advanced.serviceTiers.providerDefault'),
})))
const selectedChatProfile = computed(() => chatAgentProfiles.value.find((profile) => String(profile.id) === String(selectedChatProfileId.value)) ?? null)
const selectedChatAgent = computed(() => chatAgents.value.find((agent) => String(agent.id) === String(selectedChatAgentId.value)) ?? null)
const savingRuntimeSettings = computed(() => runtimeSettingsSavingRequests.value > 0)
const chatTemperatureLabel = computed(() => chatTemperatureOverride.value
    ? formatChatTemperature(chatTemperature.value)
    : t('tamepulse.chat.advanced.providerDefault'))
const chatAdvancedSummary = computed(() => {
    const mode = ['model', 'auto', 'profile', 'agent'].includes(chatRoutingMode.value)
        ? chatRoutingMode.value
        : 'model'
    const readOnly = Boolean(chatReadOnly.value)
    const allowLocalShell = Boolean(chatAllowLocalShell.value) && !readOnly
    const parts = [chatRoutingModeLabel(mode)]

    if (mode === 'profile' && selectedChatProfile.value?.name) {
        parts.push(selectedChatProfile.value.name)
    }

    if (mode === 'agent' && selectedChatAgent.value?.name) {
        parts.push(selectedChatAgent.value.name)
    }

    if (readOnly) {
        parts.push(t('tamepulse.chat.advanced.analysisOnly'))
    } else if (allowLocalShell) {
        parts.push(t('tamepulse.chat.advanced.allowLocalShell'))
    }

    if (chatTemperatureOverride.value) {
        parts.push(`${t('tamepulse.chat.advanced.temperature')}: ${formatChatTemperature(chatTemperature.value)}`)
    }

    if (chatServiceTier.value) {
        parts.push(t(`tamepulse.chat.advanced.serviceTiers.${chatServiceTier.value}`))
    }

    return parts.filter(Boolean).join(' · ')
})

const runtimeDetailsMessage = computed(() => {
    return [...conversationMessages.value]
        .reverse()
        .find((message) => {
            if (message?.role !== 'assistant') {
                return false
            }

            const metadata = messageMetadata(message)

            return Boolean(metadata.runtime_usage)
                || Boolean(metadata.context_snapshot)
                || Boolean(metadata.runtime_events)
        }) ?? null
})
const runtimeDetailsMetadata = computed(() => runtimeDetailsMessage.value ? messageMetadata(runtimeDetailsMessage.value) : {})
const chatUsage = computed(() => normalizeChatUsage(runtimeDetailsMetadata.value.runtime_usage))
const usagePercent = computed(() => {
    const explicitPercent = numericValue(chatUsage.value.contextPercent)

    if (explicitPercent !== null) {
        return explicitPercent
    }

    if (!chatUsage.value.inputTokens || !chatUsage.value.contextWindow) {
        return null
    }

    return (chatUsage.value.inputTokens / chatUsage.value.contextWindow) * 100
})
const usagePercentLabel = computed(() => usagePercent.value === null ? '-' : `${formatDecimal(usagePercent.value)}%`)
const contextUsageInlineLabel = computed(() => {
    if (usagePercent.value === null) {
        return ''
    }

    return t('tamepulse.chat.usage.inlineLabel', {
        percent: usagePercentLabel.value,
    })
})
const contextUsageInlineClass = computed(() => {
    if (usagePercent.value === null) return 'text-slate-400'
    if (usagePercent.value >= 90) return 'text-rose-600'
    if (usagePercent.value >= 75) return 'text-amber-600'

    return 'text-emerald-600'
})
const usageSourceLabel = computed(() => {
    if (!chatUsage.value.source) {
        return t('tamepulse.chat.usage.sources.unavailable')
    }

    return translatedOrFallback(`tamepulse.chat.usage.sources.${chatUsage.value.source}`, chatUsage.value.source)
})
const usageBarStyle = computed(() => ({
    width: `${Math.max(0, Math.min(100, usagePercent.value || 0))}%`,
}))
const usageBarClass = computed(() => {
    if (usagePercent.value === null) return 'bg-slate-300'
    if (usagePercent.value >= 90) return 'bg-rose-500'
    if (usagePercent.value >= 75) return 'bg-amber-500'
    return 'bg-emerald-500'
})
const contextSnapshotSignature = computed(() => JSON.stringify(runtimeDetailsMetadata.value.context_snapshot ?? null))
const chatContextSnapshot = computed(() => {
    if (contextSnapshotSignature.value && contextSnapshotSignature.value === contextSnapshotResetSignature.value) {
        return emptyChatContextSnapshot()
    }

    return normalizeChatContextSnapshot(runtimeDetailsMetadata.value.context_snapshot)
})
const contextItems = computed(() => chatContextSnapshot.value.items)
const contextSourceLabel = computed(() => {
    if (!chatContextSnapshot.value.source) {
        return t('tamepulse.chat.context.sources.unavailable')
    }

    return translatedOrFallback(`tamepulse.chat.context.sources.${chatContextSnapshot.value.source}`, chatContextSnapshot.value.source)
})
const detailsDrawerSubtitle = computed(() => {
    if (detailsDrawerTab.value === 'activity') {
        return t('tamepulse.chat.details.subtitles.activity', {
            count: runtimeEvents.value.length,
        })
    }

    if (!chatContextSnapshot.value.updatedAt) {
        return t('tamepulse.chat.context.drawer.subtitleUnavailable')
    }

    return t('tamepulse.chat.context.drawer.subtitle', {
        count: chatContextSnapshot.value.itemCount,
        source: contextSourceLabel.value,
        time: chatContextSnapshot.value.updatedAt,
    })
})
const usageStats = computed(() => [
    {
        key: 'inputTokens',
        label: t('tamepulse.chat.usage.inputTokens'),
        value: formatTokenCount(chatUsage.value.inputTokens),
    },
    {
        key: 'contextWindow',
        label: t('tamepulse.chat.usage.contextWindow'),
        value: formatTokenCount(chatUsage.value.contextWindow),
    },
    {
        key: 'outputTokens',
        label: t('tamepulse.chat.usage.outputTokens'),
        value: formatTokenCount(chatUsage.value.outputTokens),
    },
    {
        key: 'totalTokens',
        label: t('tamepulse.chat.usage.totalTokens'),
        value: formatTokenCount(chatUsage.value.totalTokens),
    },
])
const contextStats = computed(() => [
    {
        key: 'messages',
        label: t('tamepulse.chat.context.messages'),
        value: formatNumber(chatContextSnapshot.value.itemCount),
    },
    {
        key: 'visibleMessages',
        label: t('tamepulse.chat.context.visibleMessages'),
        value: formatNumber(chatContextSnapshot.value.visibleItemCount),
    },
    {
        key: 'characters',
        label: t('tamepulse.chat.context.characters'),
        value: formatNumber(chatContextSnapshot.value.totalCharacters),
    },
    {
        key: 'request',
        label: t('tamepulse.chat.usage.request'),
        value: runtimeDetailsMessage.value?.id ? `#${runtimeDetailsMessage.value.id}` : '-',
    },
])
const runtimeEvents = computed(() => runtimeEventsForMetadata(runtimeDetailsMetadata.value, true))
const runnerExecutionDetails = computed(() => runnerExecutionDetailsForMetadata(runtimeDetailsMetadata.value))
const runnerExecutionStatusClass = computed(() => {
    if (runnerExecutionDetails.value?.status === 'failed') {
        return 'bg-red-100 text-red-700'
    }

    if (runnerExecutionDetails.value?.status === 'completed') {
        return 'bg-emerald-100 text-emerald-700'
    }

    return 'bg-indigo-100 text-indigo-700'
})
const liveChatStatus = computed(() => {
    const message = pendingAssistantMessage.value

    if (!message) {
        return null
    }

    const step = [...chatProgressSteps(message)]
        .reverse()
        .find((item) => ['running', 'waiting'].includes(String(item.status || 'running')))

    if (!step) {
        return null
    }

    const phase = String(step.phase || '').trim()

    if (phase === 'runner_delegating') {
        const runnerDetails = runnerExecutionDetailsForMetadata(messageMetadata(message))

        if (runnerDetails?.agentName && runnerDetails?.runnerName) {
            return {
                icon: 'network-wired',
                label: t('tamepulse.chat.live.runnerWorking', {
                    agent: runnerDetails.agentName,
                    runner: runnerDetails.runnerName,
                }),
                spinning: true,
                tone: 'indigo',
            }
        }
    }

    return null
})
const liveChatStatusClass = computed(() => {
    if (liveChatStatus.value?.tone === 'amber') {
        return 'border-amber-200 bg-amber-50 text-amber-800'
    }

    if (liveChatStatus.value?.tone === 'indigo') {
        return 'border-indigo-200 bg-indigo-50 text-indigo-800'
    }

    return 'border-slate-200 bg-slate-50 text-slate-600'
})

const openDetailsDrawer = (tab = 'context') => {
    detailsDrawerTab.value = tab
    detailsDrawerOpen.value = true
}

const detailsTabClass = (tab) => {
    return detailsDrawerTab.value === tab
        ? 'bg-white text-slate-950 shadow-sm shadow-slate-950/5'
        : 'text-slate-500 hover:text-slate-900'
}

const resetChatContextSnapshot = () => {
    contextSnapshotResetSignature.value = contextSnapshotSignature.value || ''
}

const normalizeChatUsage = (usage) => {
    if (!usage || typeof usage !== 'object' || Array.isArray(usage)) {
        return emptyChatUsage()
    }

    return {
        inputTokens: numericValue(usage.input_tokens ?? usage.inputTokens ?? usage.prompt_tokens),
        outputTokens: numericValue(usage.output_tokens ?? usage.outputTokens ?? usage.completion_tokens),
        totalTokens: numericValue(usage.total_tokens ?? usage.totalTokens),
        contextWindow: numericValue(usage.context_window ?? usage.contextWindow),
        contextPercent: numericValue(usage.context_percent ?? usage.contextPercent),
        source: String(usage.source || usage.type || 'actual').trim() || 'actual',
    }
}

const emptyChatUsage = () => ({
    inputTokens: null,
    outputTokens: null,
    totalTokens: null,
    contextWindow: null,
    contextPercent: null,
    source: null,
})

const normalizeChatContextSnapshot = (snapshot) => {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
        return emptyChatContextSnapshot()
    }

    const rawItems = Array.isArray(snapshot.items)
        ? snapshot.items
        : (Array.isArray(snapshot.messages) ? snapshot.messages : [])
    const items = rawItems
        .filter((item) => item && typeof item === 'object')
        .map((item, index) => normalizeContextItem(item, index))
    const totalCharacters = numericValue(snapshot.total_characters ?? snapshot.totalCharacters)
        ?? items.reduce((total, item) => total + (numericValue(item.characters) ?? 0), 0)

    return {
        source: String(snapshot.source || '').trim() || null,
        contextWindow: numericValue(snapshot.context_window ?? snapshot.contextWindow),
        itemCount: numericValue(snapshot.item_count ?? snapshot.itemCount) ?? items.length,
        visibleItemCount: numericValue(snapshot.visible_item_count ?? snapshot.visibleItemCount) ?? items.length,
        totalCharacters,
        truncated: Boolean(snapshot.truncated),
        updatedAt: formatSnapshotTime(snapshot.updated_at ?? snapshot.updatedAt),
        items,
    }
}

const emptyChatContextSnapshot = () => ({
    source: null,
    contextWindow: null,
    itemCount: 0,
    visibleItemCount: 0,
    totalCharacters: null,
    truncated: false,
    updatedAt: null,
    items: [],
})

const normalizeContextItem = (item, index) => {
    const content = normalizeContextContent(item.content ?? item.text ?? '')
    const characters = numericValue(item.characters ?? item.total_characters ?? item.content_characters) ?? content.length

    return {
        id: item.id ?? index,
        role: String(item.role || item.type || 'message').trim() || 'message',
        type: String(item.type || item.role || 'message').trim() || 'message',
        node: String(item.node || item.node_id || item.source || '').trim(),
        content,
        characters,
        content_truncated: Boolean(item.content_truncated || item.truncated),
        tool_calls: Array.isArray(item.tool_calls) ? item.tool_calls : [],
    }
}

const normalizeContextContent = (value) => {
    if (typeof value === 'string') {
        return value
    }

    if (value === null || value === undefined) {
        return ''
    }

    return stringifyJson(value)
}

const contextItemKey = (item) => `${item.role}-${item.id}-${item.characters}`

const contextRoleLabel = (role) => translatedOrFallback(`tamepulse.chat.context.roles.${role || 'message'}`, role || 'message')

const contextItemMeta = (item) => {
    const params = {
        type: item.type || item.role || 'message',
        node: item.node || '-',
        characters: formatNumber(item.characters),
    }

    if (item.node) {
        return t('tamepulse.chat.context.itemMetaWithNode', params)
    }

    return t('tamepulse.chat.context.itemMeta', params)
}

const runtimeEventsForMetadata = (metadata, newestFirst = false) => {
    const events = Array.isArray(metadata?.runtime_events)
        ? metadata.runtime_events
        : []
    const mapped = events.map((event, index) => ({
        ...(event && typeof event === 'object' ? event : {}),
        key: `${event?.type || 'event'}-${event?.started_at || event?.finished_at || index}-${index}`,
    }))

    return newestFirst ? mapped.reverse() : mapped
}

const runnerExecutionDetailsForMetadata = (metadata) => {
    const routing = metadata?.runtime_routing && typeof metadata.runtime_routing === 'object' && !Array.isArray(metadata.runtime_routing)
        ? metadata.runtime_routing
        : {}
    const events = runtimeEventsForMetadata(metadata, true)
    const runnerEvent = events.find((event) => [
        'chat_runner_completed',
        'chat_runner_failed',
        'chat_runner_delegating',
    ].includes(String(event?.type || '')))
    const source = {
        ...(String(routing.candidate_kind || '') === 'edge_runner' ? routing : {}),
        ...(runnerEvent || {}),
    }
    const runnerName = cleanRuntimeDisplayText(source.runner_connection_name || source.runner_name || source.connection_name)

    if (!runnerName) {
        return null
    }

    const agentName = cleanRuntimeDisplayText(source.agent_name)
        || chatAgentName(source.agent_id)
        || cleanRuntimeDisplayText(source.agent_key)
    const status = runnerExecutionStatus(source)

    return {
        agentName,
        runnerName,
        status,
        statusLabel: t(`tamepulse.chat.runnerDetails.statuses.${status}`),
    }
}

const cleanRuntimeDisplayText = (value) => String(value || '').trim()

const chatAgentName = (agentId) => {
    const id = cleanRuntimeDisplayText(agentId)

    if (!id) {
        return ''
    }

    return cleanRuntimeDisplayText(chatAgents.value.find((agent) => String(agent.id) === id)?.name)
}

const runnerExecutionStatus = (event) => {
    const type = String(event?.type || '')

    if (type.includes('failed')) {
        return 'failed'
    }

    if (type.includes('completed')) {
        return 'completed'
    }

    return 'running'
}

const runtimeEventLabel = (event) => translatedOrFallback(`tamepulse.chat.events.types.${event?.type || 'event'}`, event?.type || 'event')

const runtimeEventIcon = (event) => {
    const type = String(event?.type || '')

    if (type.includes('failed')) return 'xmark'
    if (type.includes('completed') || type.includes('finished')) return 'check'
    if (type.includes('human_input')) return 'circle-question'
    if (type.includes('context')) return 'file-lines'
    if (type.includes('usage')) return 'chart-simple'
    if (type.includes('runner')) return 'network-wired'

    return 'circle'
}

const runtimeEventIconClass = (event) => {
    const type = String(event?.type || '')

    if (type.includes('failed')) return 'text-rose-500'
    if (type.includes('completed') || type.includes('finished')) return 'text-emerald-500'
    if (type.includes('human_input')) return 'text-amber-500'
    if (type.includes('runner')) return 'text-indigo-500'

    return 'text-slate-400'
}

const runtimeEventTime = (event) => {
    return formatSnapshotTime(event?.finished_at ?? event?.started_at ?? event?.requested_at ?? event?.submitted_at ?? event?.resumed_at) || '-'
}

const runtimeEventDetails = (event) => {
    const detailKeys = [
        'routing_mode',
        'model',
        'candidate_id',
        'candidate_kind',
        'agent_id',
        'agent_key',
        'runner_binding_id',
        'runner_connection_id',
        'duration_ms',
        'question',
        'error',
    ]
    const details = {}

    detailKeys.forEach((key) => {
        if (event?.[key] !== null && event?.[key] !== undefined && event?.[key] !== '') {
            details[key] = event[key]
        }
    })

    return Object.keys(details).length ? stringifyJson(details) : ''
}

const translatedOrFallback = (key, fallback) => {
    const translated = t(key)

    return translated === key ? String(fallback || '') : translated
}

const numericValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
}

const formatTokenCount = (value) => {
    const number = numericValue(value)

    return number === null ? '-' : formatNumber(number)
}

const formatNumber = (value) => {
    const number = numericValue(value)

    if (number === null) {
        return '-'
    }

    return new Intl.NumberFormat(locale.value).format(number)
}

const formatDecimal = (value) => {
    const number = numericValue(value)

    if (number === null) {
        return '-'
    }

    return new Intl.NumberFormat(locale.value, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(number)
}

const formatSnapshotTime = (value) => {
    if (!value) {
        return null
    }

    const numeric = numericValue(value)
    const date = numeric !== null
        ? new Date(numeric > 10_000_000_000 ? numeric : numeric * 1000)
        : new Date(value)

    if (Number.isNaN(date.getTime())) {
        return null
    }

    return date.toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

const stringifyJson = (value) => {
    try {
        return JSON.stringify(value, null, 2)
    } catch (error) {
        return String(value ?? '')
    }
}

const assistantMessagesAfterLatestUser = (messages) => {
    const latestUserIndex = messages.reduce((latestIndex, message, index) => (
        message.role === 'user' ? index : latestIndex
    ), -1)

    return latestUserIndex >= 0 ? messages.slice(latestUserIndex + 1) : messages
}

const focusMessageInput = () => {
    nextTick(() => {
        messageInput.value?.focus()
    })
}

const defaultChatRuntimeSettings = () => ({
    advanced_enabled: true,
    mode: 'model',
    profile_id: '',
    agent_id: '',
    read_only: true,
    allow_local_shell: false,
    temperature_enabled: false,
    temperature: 0,
    service_tier: '',
})

const normalizeChatRuntimeSettings = (settings = {}) => {
    const source = settings && typeof settings === 'object' ? settings : {}
    const mode = ['model', 'auto', 'profile', 'agent'].includes(source.mode)
        ? source.mode
        : 'model'
    const readOnly = Object.prototype.hasOwnProperty.call(source, 'read_only')
        ? Boolean(source.read_only)
        : true
    const temperatureEnabled = Object.prototype.hasOwnProperty.call(source, 'temperature_enabled')
        ? Boolean(source.temperature_enabled)
        : source.temperature !== undefined && source.temperature !== null && source.temperature !== ''

    return {
        advanced_enabled: true,
        mode,
        profile_id: mode === 'profile' ? String(source.profile_id || '') : '',
        agent_id: mode === 'agent' ? String(source.agent_id || '') : '',
        read_only: readOnly,
        allow_local_shell: !readOnly && Boolean(source.allow_local_shell),
        temperature_enabled: temperatureEnabled,
        temperature: temperatureEnabled ? normalizeChatTemperature(source.temperature) : 0,
        service_tier: ['default', 'fast'].includes(source.service_tier) ? source.service_tier : '',
    }
}

const applyChatRuntimeSettings = (settings = {}) => {
    const normalized = normalizeChatRuntimeSettings(settings)

    applyingChatRuntimeSettings = true
    chatRoutingMode.value = normalized.mode
    selectedChatProfileId.value = normalized.profile_id
    selectedChatAgentId.value = normalized.agent_id
    chatReadOnly.value = normalized.read_only
    chatAllowLocalShell.value = normalized.allow_local_shell
    chatTemperatureOverride.value = normalized.temperature_enabled
    chatTemperature.value = normalized.temperature
    chatServiceTier.value = normalized.service_tier
    nextTick(() => {
        applyingChatRuntimeSettings = false
    })
}

const handleAssistantActionEnter = () => {
    if (draftHasContent.value) {
        handleComposerSubmit()
        return
    }

    focusMessageInput()
}

const handleComposerSubmit = () => {
    if (composerActionIsCancel.value) {
        cancelCurrentRun()
        return
    }

    sendMessage()
}

const startNewConversation = () => {
    selectedConversationId.value = null
    messageText.value = ''
    selectedFiles.value = []
    localError.value = ''
    contextSnapshotResetSignature.value = ''
    applyChatRuntimeSettings(defaultChatRuntimeSettings())
    awaitingAutoMailDraftConversationId.value = null
    awaitingAutoQuoteEntityConversationId.value = null
    pendingAutoQuoteEntityPatchRequest.value = null
    leaveChatChannel()
}

const selectConversation = (conversation) => {
    const previousConversationId = selectedConversationId.value

    selectedConversationId.value = conversation.id
    localError.value = ''
    contextSnapshotResetSignature.value = ''
    applyChatRuntimeSettings(conversation.runtime_settings || defaultChatRuntimeSettings())

    if (String(previousConversationId || '') !== String(conversation.id || '')) {
        awaitingAutoMailDraftConversationId.value = null
        awaitingAutoQuoteEntityConversationId.value = null
        pendingAutoQuoteEntityPatchRequest.value = null
    }
}

const selectConversationById = (conversationId) => {
    if (!conversationId) {
        startNewConversation()
        return
    }

    const conversation = conversations.value.find((item) => String(item.id) === String(conversationId))

    if (conversation) {
        selectConversation(conversation)
    }
}

const currentQuoteEntityFormContext = () => {
    if (route.name === 'quote.account.create' || route.name === 'quote.account.edit') {
        return {
            entity: 'account',
            mode: route.name === 'quote.account.edit' ? 'edit' : 'create',
            id: route.name === 'quote.account.edit' ? String(route.params.id || '') : '',
            editable_fields: QUOTE_ACCOUNT_EDITABLE_FIELDS,
        }
    }

    if (route.name === 'quote.person.create' || route.name === 'quote.person.edit') {
        return {
            entity: 'person',
            mode: route.name === 'quote.person.edit' ? 'edit' : 'create',
            id: route.name === 'quote.person.edit' ? String(route.params.id || '') : '',
            editable_fields: QUOTE_PERSON_EDITABLE_FIELDS,
        }
    }

    return null
}

const currentChatClientContext = () => {
    const context = {
        route: {
            name: route.name ? String(route.name) : '',
            params: {
                tenantId: route.params.tenantId ? String(route.params.tenantId) : '',
                id: route.params.id ? String(route.params.id) : '',
            },
        },
    }
    const quoteEntityForm = currentQuoteEntityFormContext()

    if (quoteEntityForm) {
        context.quote_entity_form = quoteEntityForm
    }

    const chatRouting = currentChatRoutingContext()

    if (chatRouting) {
        context.chat_routing = chatRouting
    }

    return context
}

const currentChatRoutingContext = () => {
    const mode = ['model', 'auto', 'profile', 'agent'].includes(chatRoutingMode.value)
        ? chatRoutingMode.value
        : 'model'
    const payload = {
        advanced_enabled: true,
        mode,
        read_only: Boolean(chatReadOnly.value),
        allow_local_shell: Boolean(chatAllowLocalShell.value) && !chatReadOnly.value,
        temperature_enabled: Boolean(chatTemperatureOverride.value),
    }

    if (mode === 'profile') {
        payload.profile_id = selectedChatProfileId.value || null
    }

    if (mode === 'agent') {
        payload.agent_id = selectedChatAgentId.value || null
    }

    if (chatTemperatureOverride.value) {
        payload.temperature = normalizeChatTemperature(chatTemperature.value)
    }

    if (chatServiceTier.value) {
        payload.service_tier = chatServiceTier.value
    }

    return payload
}

const saveConversationRuntimeSettings = async (conversationId, runtimeSettings) => {
    if (!conversationId) {
        return
    }

    runtimeSettingsSavingRequests.value += 1

    try {
        const response = await updateRuntimeSettingsMutation({
            conversation_id: conversationId,
            runtime_settings: runtimeSettings,
        })
        const conversation = response?.data?.tamepulseChatUpdateRuntimeSettings

        if (conversation) {
            replaceConversation(conversation)
            if (localError.value === t('tamepulse.chat.runtimeSettingsSaveFailed')) {
                localError.value = ''
            }
        }
    } catch (error) {
        localError.value = error?.graphQLErrors?.[0]?.message || error?.message || t('tamepulse.chat.runtimeSettingsSaveFailed')
    } finally {
        runtimeSettingsSavingRequests.value = Math.max(0, runtimeSettingsSavingRequests.value - 1)
    }
}

const scheduleRuntimeSettingsSave = () => {
    if (applyingChatRuntimeSettings || !selectedConversationId.value) {
        return
    }

    const conversationId = selectedConversationId.value
    const runtimeSettings = currentChatRoutingContext()

    if (runtimeSettingsSaveTimeout) {
        window.clearTimeout(runtimeSettingsSaveTimeout)
    }

    runtimeSettingsSaveTimeout = window.setTimeout(() => {
        runtimeSettingsSaveTimeout = null
        saveConversationRuntimeSettings(conversationId, runtimeSettings)
    }, 500)
}

const validateChatRouting = () => {
    if (chatRoutingMode.value === 'profile' && !selectedChatProfileId.value) {
        localError.value = t('tamepulse.chat.advanced.profileRequired')
        return false
    }

    if (chatRoutingMode.value === 'agent' && !selectedChatAgentId.value) {
        localError.value = t('tamepulse.chat.advanced.agentRequired')
        return false
    }

    return true
}

const chatRoutingModeLabel = (mode) => {
    const key = `tamepulse.chat.advanced.modes.${mode || 'model'}`
    const translated = t(key)

    return translated === key ? String(mode || 'model') : translated
}

const normalizeChatTemperature = (value) => Math.max(0, Math.min(2, Number(value) || 0))

const formatChatTemperature = (value) => new Intl.NumberFormat(locale.value, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
}).format(normalizeChatTemperature(value))

const sendMessage = async () => {
    if (sending.value || composerActionIsCancel.value) {
        return
    }

    localError.value = ''

    if (!messageText.value.trim() && selectedFiles.value.length === 0) {
        localError.value = t('tamepulse.chat.messageRequired')
        return
    }

    if (!validateChatRouting()) {
        return
    }

    sending.value = true

    try {
        const response = await sendMessageMutation({
            conversation_id: selectedConversationId.value,
            content: messageText.value,
            files: selectedFiles.value,
            context: currentChatClientContext(),
        })

        const conversation = response?.data?.tamepulseChatSendMessage
        if (conversation) {
            upsertConversation(conversation)
            selectedConversationId.value = conversation.id
            awaitingAutoMailDraftConversationId.value = String(conversation.id)
            awaitingAutoQuoteEntityConversationId.value = String(conversation.id)
            pendingAutoQuoteEntityPatchRequest.value = {
                conversationId: String(conversation.id),
                createdAt: Date.now(),
            }
            subscribeToConversation(conversation.id)
            await loadLatestMessages(conversation.id)
        }

        messageText.value = ''
        selectedFiles.value = []

        if (fileInput.value) {
            fileInput.value.value = ''
        }

        scrollToBottom()
    } catch (error) {
        localError.value = error?.graphQLErrors?.[0]?.message || error?.message || t('tamepulse.chat.sendFailed')
    } finally {
        sending.value = false
    }
}

const cancelCurrentRun = async () => {
    const assistantMessage = pendingAssistantMessage.value

    if (!assistantMessage || cancellingRun.value) {
        return
    }

    localError.value = ''
    cancellingRun.value = true

    try {
        const response = await cancelRunMutation({
            assistant_message_id: assistantMessage.id,
        })
        const message = response?.data?.tamepulseChatCancelRun

        if (message?.conversation_id) {
            mergeConversationMessages(message.conversation_id, [message])
            refreshConversationsSilently().catch(() => null)
        }
    } catch (error) {
        localError.value = error?.graphQLErrors?.[0]?.message || error?.message || t('tamepulse.chat.cancelFailed')
    } finally {
        cancellingRun.value = false
    }
}

const submitHumanInput = async (message) => {
    const request = pendingHumanInput(message)
    const draft = humanInputDraft(message)
    const selectedChoice = humanInputChoices(message).find((choice) => choice.id === draft.choice_id) ?? null
    const text = String(draft.text || '').trim()
    const canUseChoice = selectedChoice && draft.choice_id !== HUMAN_OTHER_CHOICE_ID

    if (!request || submittingHumanInput.value[message.id]) {
        return
    }

    if (!text && !canUseChoice) {
        updateHumanInputDraft(message, {
            error: t('tamepulse.chat.humanInput.required'),
        })
        return
    }

    submittingHumanInput.value = {
        ...submittingHumanInput.value,
        [message.id]: true,
    }

    try {
        const response = await submitHumanInputMutation({
            assistant_message_id: message.id,
            input: {
                choice_id: canUseChoice ? selectedChoice.id : (draft.choice_id === HUMAN_OTHER_CHOICE_ID ? HUMAN_OTHER_CHOICE_ID : null),
                text: text || selectedChoice?.label || '',
            },
        })
        const updatedMessage = response?.data?.tamepulseChatSubmitHumanInput

        if (updatedMessage?.conversation_id) {
            mergeConversationMessages(updatedMessage.conversation_id, [updatedMessage])
            refreshConversationsSilently().catch(() => null)
            humanInputDrafts.value = Object.fromEntries(
                Object.entries(humanInputDrafts.value).filter(([key]) => key !== String(message.id)),
            )
        }
    } catch (error) {
        updateHumanInputDraft(message, {
            error: error?.graphQLErrors?.[0]?.message || error?.message || t('tamepulse.chat.humanInput.submitFailed'),
        })
    } finally {
        const next = { ...submittingHumanInput.value }
        delete next[message.id]
        submittingHumanInput.value = next
    }
}

const upsertConversation = (conversation) => {
    if (!conversation || !result.value) return

    const existing = result.value.tamepulseChatConversations ?? []
    const next = [
        conversation,
        ...existing.filter((item) => String(item.id) !== String(conversation.id)),
    ]

    result.value = {
        ...result.value,
        tamepulseChatConversations: next,
    }
}

const replaceConversation = (conversation) => {
    if (!conversation || !result.value) return

    const existing = result.value.tamepulseChatConversations ?? []
    const found = existing.some((item) => String(item.id) === String(conversation.id))
    const next = found
        ? existing.map((item) => String(item.id) === String(conversation.id) ? conversation : item)
        : [conversation, ...existing]

    result.value = {
        ...result.value,
        tamepulseChatConversations: next,
    }
}

const removeConversation = (conversationId) => {
    if (!result.value) return

    const key = conversationKey(conversationId)
    const existing = result.value.tamepulseChatConversations ?? []
    const next = existing.filter((item) => String(item.id) !== String(conversationId))
    const nextMessagesById = { ...conversationMessagesById.value }
    const nextHasMoreMessages = { ...conversationHasMoreMessages.value }
    delete nextMessagesById[key]
    delete nextHasMoreMessages[key]

    result.value = {
        ...result.value,
        tamepulseChatConversations: next,
    }
    conversationMessagesById.value = nextMessagesById
    conversationHasMoreMessages.value = nextHasMoreMessages
}

const deleteConversation = async (conversation, closeModal) => {
    if (!conversation?.id || deletingConversationIds.value[conversation.id]) {
        return
    }

    const conversationId = conversation.id
    const nextConversation = conversations.value.find((item) => String(item.id) !== String(conversationId)) ?? null

    deletingConversationIds.value = {
        ...deletingConversationIds.value,
        [conversationId]: true,
    }
    localError.value = ''

    try {
        await deleteConversationMutation({ id: conversationId })
        closeModal?.()
        removeConversation(conversationId)

        if (String(selectedConversationId.value) === String(conversationId)) {
            if (nextConversation) {
                selectConversation(nextConversation)
            } else {
                startNewConversation()
            }
        }
    } catch (error) {
        localError.value = error?.graphQLErrors?.[0]?.message || error?.message || t('tamepulse.chat.deleteConversation.failed')
    } finally {
        const next = { ...deletingConversationIds.value }
        delete next[conversationId]
        deletingConversationIds.value = next
    }
}

const refreshConversationsSilently = async () => {
    const { data } = await apolloClient.query({
        query: chatConversationsQuery,
        context: { suppressLoading: true },
        fetchPolicy: 'network-only',
    })

    result.value = {
        ...(result.value ?? {}),
        tamepulseChatConversations: data?.tamepulseChatConversations ?? [],
    }
}

const subscribeToConversation = (conversationId) => {
    leaveChatChannel()

    if (!window.Echo || !conversationId) {
        realtimeAvailable.value = Boolean(window.Echo)
        return
    }

    const channelName = `tamepulse.chat.${conversationId}`
    chatChannelName.value = channelName

    window.Echo.channel(channelName)
        .listen('.TamepulseChatConversationUpdated', (event) => {
            refreshConversationsSilently()
                .catch(() => null)

            const conversationId = event?.conversation_id ?? selectedConversationId.value

            if (String(conversationId) === String(selectedConversationId.value)) {
                loadLatestMessages(conversationId, { scrollToEnd: isNearBottom() }).catch(() => null)
            }
        })

    realtimeAvailable.value = window.Echo?.connector?.pusher?.connection?.state === 'connected'
}

const leaveChatChannel = () => {
    if (window.Echo && chatChannelName.value) {
        window.Echo.leave(chatChannelName.value)
    }

    chatChannelName.value = null
}

const onFilesSelected = (event) => {
    selectedFiles.value = [
        ...selectedFiles.value,
        ...Array.from(event.target.files ?? []),
    ]
}

const removeFile = (index) => {
    selectedFiles.value = selectedFiles.value.filter((_, fileIndex) => fileIndex !== index)
}

const loadLatestMessages = async (conversationId, options = {}) => {
    await loadMessagesPage(conversationId, {
        replace: options.replace ?? false,
        scrollToEnd: options.scrollToEnd ?? true,
    })
}

const loadOlderMessages = async () => {
    if (!selectedConversationId.value || !hasOlderMessages.value || loadingOlderMessages.value) return

    const oldestMessage = conversationMessages.value[0]
    if (!oldestMessage) return

    loadingOlderMessages.value = true

    try {
        await loadMessagesPage(selectedConversationId.value, {
            beforeId: oldestMessage.id,
            preserveScroll: true,
        })
    } finally {
        loadingOlderMessages.value = false
    }
}

const loadMessagesPage = async (conversationId, options = {}) => {
    if (!conversationId) return

    const key = conversationKey(conversationId)
    const beforeId = options.beforeId ?? null
    const previousMessages = conversationMessagesById.value[key] ?? []
    const pane = messagesPane.value
    const previousScrollHeight = pane?.scrollHeight ?? 0
    const previousScrollTop = pane?.scrollTop ?? 0

    const { data } = await apolloClient.query({
        query: chatMessagesQuery,
        variables: {
            conversation_id: conversationId,
            before_id: beforeId,
            limit: MESSAGE_PAGE_SIZE,
        },
        context: { suppressLoading: true },
        fetchPolicy: 'network-only',
    })

    const page = data?.tamepulseChatMessages
    if (!page) return

    mergeConversationMessages(conversationId, page.messages ?? [], {
        replace: Boolean(options.replace),
        hasMore: page.has_more,
        updateHasMore: Boolean(beforeId) || Boolean(options.replace) || previousMessages.length === 0,
    })

    await nextTick()

    if (String(conversationId) === String(selectedConversationId.value)) {
        maybeAutoOpenMailDraft()
        maybeAutoApplyQuoteEntityPatch()
        maybeAutoOpenQuoteEntity()
    }

    if (options.preserveScroll && pane) {
        pane.scrollTop = pane.scrollHeight - previousScrollHeight + previousScrollTop
    } else if (options.scrollToEnd && String(conversationId) === String(selectedConversationId.value)) {
        scrollToBottom()
    }
}

const mergeConversationMessages = (conversationId, messages, options = {}) => {
    const key = conversationKey(conversationId)
    const existing = options.replace ? [] : (conversationMessagesById.value[key] ?? [])
    const byId = new Map()

    ;[...existing, ...messages].forEach((message) => {
        byId.set(String(message.id), {
            ...(byId.get(String(message.id)) ?? {}),
            ...message,
        })
    })

    conversationMessagesById.value = {
        ...conversationMessagesById.value,
        [key]: [...byId.values()].sort((a, b) => Number(a.id) - Number(b.id)),
    }

    if (options.updateHasMore) {
        conversationHasMoreMessages.value = {
            ...conversationHasMoreMessages.value,
            [key]: Boolean(options.hasMore),
        }
    }
}

const onMessagesScroll = () => {
    if (!messagesPane.value || messagesPane.value.scrollTop > 80) return

    loadOlderMessages().catch(() => null)
}

const visibleAttachments = (attachments) => {
    return (attachments ?? []).map((attachment) => ({
        name: attachment.name,
        size: attachment.size,
        included_in_prompt: Boolean(attachment.included_in_prompt),
    }))
}

const messageMetadata = (message) => {
    const metadata = message?.metadata

    if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
        return metadata
    }

    if (typeof metadata !== 'string' || !metadata.trim()) {
        return {}
    }

    try {
        const parsed = JSON.parse(metadata)

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch (error) {
        return {}
    }
}

const pendingHumanInput = (message) => {
    if (message?.role !== 'assistant' || message?.status !== 'waiting') {
        return null
    }

    const pending = messageMetadata(message).pending_human_input
    if (!pending || typeof pending !== 'object' || Array.isArray(pending) || pending.status !== 'pending') {
        return null
    }

    return {
        question: String(pending.question || ''),
        choices: normalizeHumanInputChoices(pending.choices),
        allowOther: pending.allow_other !== false,
    }
}

const normalizeHumanInputChoices = (choices) => {
    if (!Array.isArray(choices)) {
        return []
    }

    return choices
        .map((choice, index) => {
            if (choice && typeof choice === 'object' && !Array.isArray(choice)) {
                const label = String(choice.label || choice.title || choice.text || choice.value || choice.id || '').trim()
                const value = String(choice.value || choice.id || label).trim()
                const id = String(choice.id || value || index + 1).trim()

                return {
                    id,
                    value,
                    label,
                    description: String(choice.description || choice.message || '').trim(),
                }
            }

            const label = String(choice || '').trim()

            return {
                id: label || String(index + 1),
                value: label,
                label,
                description: '',
            }
        })
        .filter((choice) => choice.label)
}

const humanInputChoices = (message) => pendingHumanInput(message)?.choices ?? []

const humanInputAllowsOther = (message) => pendingHumanInput(message)?.allowOther !== false

const humanInputDraft = (message) => {
    const key = String(message?.id || '')

    return humanInputDrafts.value[key] ?? {
        choice_id: '',
        text: '',
        error: '',
    }
}

const updateHumanInputDraft = (message, patch) => {
    const key = String(message?.id || '')
    if (!key) {
        return
    }

    humanInputDrafts.value = {
        ...humanInputDrafts.value,
        [key]: {
            ...humanInputDraft(message),
            ...patch,
        },
    }
}

const selectHumanInputChoice = (message, choiceId) => {
    updateHumanInputDraft(message, {
        choice_id: choiceId,
        text: choiceId === HUMAN_OTHER_CHOICE_ID ? humanInputDraft(message).text : '',
        error: '',
    })
}

const updateHumanInputText = (message, text) => {
    updateHumanInputDraft(message, {
        text,
        error: '',
    })
}

const humanInputTextVisible = (message) => {
    const request = pendingHumanInput(message)
    if (!request) {
        return false
    }

    const draft = humanInputDraft(message)

    return request.choices.length === 0 || (request.allowOther && draft.choice_id === HUMAN_OTHER_CHOICE_ID)
}

const humanInputChoiceButtonClass = (message, choiceId) => {
    return humanInputDraft(message).choice_id === choiceId
        ? 'border-amber-500 bg-white text-amber-950 shadow-sm shadow-amber-950/10'
        : 'border-amber-200 bg-amber-100/60 text-amber-900 hover:border-amber-300 hover:bg-white'
}

const messageBubbleClass = (message) => [
    isCompact.value ? 'max-w-[88%] px-3.5 py-3' : 'max-w-[min(760px,85%)] px-4 py-3',
    message.role === 'user'
        ? 'rounded-br-md bg-blue-600/95 text-white shadow-sm shadow-blue-900/20'
        : message.status === 'failed'
            ? 'rounded-bl-md border border-red-200 bg-red-50 text-red-950 shadow-sm shadow-red-950/5'
            : message.status === 'cancelled'
                ? 'rounded-bl-md border border-slate-200 bg-slate-100 text-slate-600 shadow-sm shadow-slate-950/5'
                : 'rounded-bl-md border border-slate-200/80 bg-white text-slate-900 shadow-[0_10px_28px_rgba(15,23,42,0.06)]',
]

const chatProgressSteps = (message) => {
    const metadata = messageMetadata(message)
    const progress = Array.isArray(metadata.progress) ? metadata.progress : []

    return progress
        .filter((step) => step && typeof step === 'object' && step.step)
        .map((step, index) => ({
            ...step,
            key: `${step.step}-${index}`,
        }))
}

const chatProgressLabel = (step) => {
    const stepName = String(step?.step || '')
    const status = String(step?.status || 'running')
    const phase = status === 'running' ? String(step?.phase || '').trim() : ''
    const params = {
        count: Number(step?.count ?? 0),
        toolCount: Array.isArray(step?.tools) ? step.tools.length : 0,
    }
    const phaseKey = phase ? `tamepulse.chat.progress.${stepName}.${phase}` : ''
    const statusKey = `tamepulse.chat.progress.${stepName}.${status}`
    const fallbackKey = `tamepulse.chat.progress.${status}`

    if (phaseKey) {
        const phaseLabel = t(phaseKey, params)

        if (phaseLabel !== phaseKey) {
            return phaseLabel
        }
    }

    const statusLabel = t(statusKey, params)

    if (statusLabel !== statusKey) {
        return statusLabel
    }

    return t(fallbackKey, params)
}

const chatProgressIcon = (step) => {
    if (step?.status === 'completed') return 'check'
    if (step?.status === 'failed') return 'xmark'
    if (step?.status === 'cancelled') return 'ban'
    if (step?.status === 'waiting') return 'circle-question'
    if (step?.status === 'skipped') return 'circle'

    return 'spinner'
}

const chatProgressIconClass = (step) => {
    if (step?.status === 'completed') return 'text-emerald-500'
    if (step?.status === 'failed') return 'text-red-500'
    if (step?.status === 'cancelled') return 'text-slate-400'
    if (step?.status === 'waiting') return 'text-amber-500'
    if (step?.status === 'skipped') return 'text-slate-300'

    return 'animate-spin text-blue-500'
}

const knowledgeSources = (message) => {
    if (message?.role !== 'assistant') {
        return []
    }

    const metadata = messageMetadata(message)
    const results = Array.isArray(metadata.rag_results) ? metadata.rag_results : []
    const sources = new Map()

    results.forEach((result) => {
        const nodeId = result?.knowledge_node_id

        if (!nodeId) {
            return
        }

        const key = `node:${nodeId}`

        if (sources.has(key)) {
            return
        }

        sources.set(key, {
            key,
            nodeId: String(nodeId),
            label: knowledgeSourceLabel(result),
        })
    })

    return [...sources.values()]
}

const knowledgeSourceLabel = (source) => {
    const filename = String(source?.filename || '').trim()

    if (filename) {
        return filename
    }

    const relativePath = String(source?.relative_path || '').trim()

    if (!relativePath) {
        return t('tamepulse.chat.sourceFallback')
    }

    return relativePath.split('/').filter(Boolean).pop() || relativePath
}

const knowledgeSourceRoute = (source) => ({
    name: 'tamepulse.knowledgeBase.index',
    params: { tenantId: route.params.tenantId },
    query: { node_id: source.nodeId },
})

const mailDraftActions = (message) => {
    if (message?.role !== 'assistant' || message?.status !== 'completed') {
        return []
    }

    const metadata = messageMetadata(message)
    const results = Array.isArray(metadata.tool_results) ? metadata.tool_results : []

    return results
        .map((result, index) => {
            const action = result?.action
            const draft = result?.draft

            if (!draft || action?.type !== 'open_mail_composer') {
                return null
            }

            const presentation = result.presentation || action.presentation || 'cta'

            return {
                key: `${message.id}:mail-draft:${index}`,
                presentation,
                draft,
                action,
                label: action.label || t('tamepulse.chat.openDraftInMail'),
            }
        })
        .filter(Boolean)
}

const visibleMailDraftActions = (message) => mailDraftActions(message)
    .filter((action) => action.presentation !== 'auto_open' || !processedMailDraftActionKeys.value[action.key])

const quoteEntityActions = (message) => {
    if (message?.role !== 'assistant' || message?.status !== 'completed') {
        return []
    }

    const metadata = messageMetadata(message)
    const results = Array.isArray(metadata.tool_results) ? metadata.tool_results : []
    const actions = []

    results.forEach((result, resultIndex) => {
        const resultActions = [
            result?.action,
            ...(Array.isArray(result?.actions) ? result.actions : []),
        ].filter(Boolean)

        resultActions.forEach((action, actionIndex) => {
            const normalized = normalizeQuoteEntityAction(action)

            if (!normalized) {
                return
            }

            actions.push({
                ...normalized,
                key: `${message.id}:quote-entity:${resultIndex}:${actionIndex}:${normalized.entity}:${normalized.mode}:${normalized.id || 'new'}`,
                label: normalized.label || quoteEntityActionFallbackLabel(normalized),
            })
        })
    })

    return actions
}

const visibleQuoteEntityActions = (message) => quoteEntityActions(message)

const quoteEntityPatchActions = (message) => {
    if (message?.role !== 'assistant' || message?.status !== 'completed') {
        return []
    }

    const metadata = messageMetadata(message)
    const results = Array.isArray(metadata.tool_results) ? metadata.tool_results : []
    const actions = []

    results.forEach((result, resultIndex) => {
        const resultActions = [
            result?.action,
            ...(Array.isArray(result?.actions) ? result.actions : []),
        ].filter(Boolean)

        resultActions.forEach((action, actionIndex) => {
            const normalized = normalizeQuoteEntityPatchAction(action)

            if (!normalized) {
                return
            }

            actions.push({
                ...normalized,
                key: `${message.id}:quote-entity-patch:${resultIndex}:${actionIndex}:${normalized.entity}`,
            })
        })
    })

    return actions
}

const normalizeQuoteEntityAction = (action) => {
    if (!action || action.type !== 'open_quote_entity') {
        return null
    }

    const entity = String(action.entity || '').trim()
    const mode = String(action.mode || '').trim()

    if (!['person', 'account'].includes(entity) || !['create', 'edit'].includes(mode)) {
        return null
    }

    if (mode === 'edit' && !action.id) {
        return null
    }

    return {
        ...action,
        entity,
        mode,
        id: action.id === null || action.id === undefined ? '' : String(action.id),
        presentation: action.presentation || 'cta',
        form: action.form && typeof action.form === 'object' ? action.form : null,
    }
}

const normalizeQuoteEntityPatchAction = (action) => {
    if (!action || action.type !== 'patch_quote_entity_form') {
        return null
    }

    const entity = String(action.entity || '').trim()

    if (!['person', 'account'].includes(entity) || !action.form || typeof action.form !== 'object') {
        return null
    }

    return {
        ...action,
        entity,
        presentation: action.presentation || 'auto_apply',
        form: action.form,
    }
}

const quoteEntityActionFallbackLabel = (action) => {
    if (action.entity === 'person') {
        return action.mode === 'edit'
            ? t('tamepulse.chat.openPersonInQuote')
            : t('tamepulse.chat.openPersonForm')
    }

    return action.mode === 'edit'
        ? t('tamepulse.chat.openAccountInQuote')
        : t('tamepulse.chat.openAccountForm')
}

const quoteEntityActionIcon = (action) => action.entity === 'person' ? 'user' : 'building'

const mailMessageContentParts = (message) => {
    const content = String(message?.content || '')

    if (!content) {
        return []
    }

    const actions = mailMessageActions(message)

    if (!actions.length) {
        return [{
            key: `${message.id}:content`,
            content,
            actions: [],
        }]
    }

    const placements = mailMessageActionPlacements(content, actions)

    const groupedPlacements = new Map()

    placements.forEach((placement) => {
        const key = String(placement.index)
        groupedPlacements.set(key, [
            ...(groupedPlacements.get(key) || []),
            placement.action,
        ])
    })

    const parts = []
    let cursor = 0
    let index = 0

    ;[...groupedPlacements.entries()]
        .map(([position, placementActions]) => ({
            position: Number(position),
            actions: placementActions,
        }))
        .sort((a, b) => a.position - b.position)
        .forEach((placement) => {
            const markdown = content.slice(cursor, placement.position).trimEnd()

            if (markdown) {
                parts.push({
                    key: `${message.id}:mail-content:${index++}`,
                    content: markdown,
                    actions: [],
                })
            }

            parts.push({
                key: `${message.id}:mail-actions:${index++}`,
                content: '',
                actions: placement.actions,
            })

            cursor = placement.position
        })

    const remaining = content.slice(cursor).trimStart()

    if (remaining) {
        parts.push({
            key: `${message.id}:mail-content:${index++}`,
            content: remaining,
            actions: [],
        })
    }

    return parts.length ? parts : [{
        key: `${message.id}:content`,
        content,
        actions: [],
    }]
}

const mailMessageActions = (message) => {
    if (message?.role !== 'assistant' || message?.status !== 'completed') {
        return []
    }

    const metadata = messageMetadata(message)
    const results = Array.isArray(metadata.tool_results) ? metadata.tool_results : []
    const seen = new Set()
    const actions = []

    results.forEach((result, resultIndex) => {
        const resultActions = Array.isArray(result?.actions) ? result.actions : []

        resultActions.forEach((action, actionIndex) => {
            if (action?.type !== 'open_mail_message') {
                return
            }

            const target = normalizeMailMessageTarget(action.message)

            if (!target) {
                return
            }

            const key = mailMessageTargetKey(target)

            if (seen.has(key)) {
                return
            }

            seen.add(key)
            actions.push({
                key: `${message.id}:mail-message:${resultIndex}:${actionIndex}:${key}`,
                target,
                action,
                message: action.message,
                label: action.label || t('tamepulse.chat.openMessageInMail'),
            })
        })
    })

    return actions
}

const visibleMailMessageActions = (message) => {
    const placedKeys = placedMailMessageActionKeys(message)

    return mailMessageActions(message)
        .filter((action) => !placedKeys.has(action.key))
}

const placedMailMessageActionKeys = (message) => {
    const keys = new Set()

    mailMessageContentParts(message)
        .forEach((part) => {
            part.actions.forEach((action) => keys.add(action.key))
        })

    return keys
}

const mailMessageActionPlacements = (content, actions) => {
    const sections = mailMessageResultSections(content)
    const placements = []
    const seen = new Set()

    actions.forEach((action, actionIndex) => {
        const matchIndex = mailMessageActionMatchIndex(content, action)
        const fallbackSection = sections[actionIndex]

        if (matchIndex < 0 && !fallbackSection) {
            return
        }

        const section = matchIndex >= 0
            ? mailMessageSectionForIndex(content, sections, matchIndex)
            : {
                start: fallbackSection.start,
                end: fallbackSection.end,
            }
        const insertionIndex = mailMessageSectionInsertionIndex(content, section.start, section.end)
        const placementKey = `${insertionIndex}:${action.key}`

        if (seen.has(placementKey)) {
            return
        }

        seen.add(placementKey)
        placements.push({
            index: insertionIndex,
            action,
        })
    })

    const placedActionKeys = new Set(placements.map((placement) => placement.action.key))

    actions
        .filter((action) => !placedActionKeys.has(action.key))
        .forEach((action) => {
            placements.push({
                index: content.length,
                action,
            })
        })

    return placements
}

const mailMessageResultSections = (content) => {
    const idSections = mailMessageIdSections(content)

    if (idSections.length) {
        return idSections
    }

    return mailMessageHeadingSections(content)
}

const mailMessageIdSections = (content) => {
    const ids = []
    const pattern = /(^|\n)[ \t]*(?:[-*+][ \t]+)?(?:\*\*)?ID\s*[:：](?:\*\*)?.*$/gimu
    let match

    while ((match = pattern.exec(content)) !== null) {
        ids.push({
            start: mailMessageSectionStartBefore(content, match.index),
            idStart: match.index,
        })
    }

    return ids.map((item, index) => ({
        start: item.start,
        end: ids[index + 1]?.start ?? content.length,
    }))
}

const mailMessageHeadingSections = (content) => {
    const headings = []
    const pattern = /^[ \t]*\d+[\.)][ \t]+.+$/gm
    let match

    while ((match = pattern.exec(content)) !== null) {
        headings.push({
            start: match.index,
            end: match.index + match[0].length,
        })
    }

    return headings.map((heading, index) => ({
        start: heading.start,
        end: headings[index + 1]?.start ?? content.length,
    }))
}

const mailMessageSectionStartBefore = (content, index) => {
    const before = content.slice(0, index)
    const blankLineIndex = before.lastIndexOf('\n\n')

    return blankLineIndex >= 0 ? blankLineIndex + 2 : 0
}

const mailMessageActionMatchIndex = (content, action) => {
    const message = action?.message || {}
    const id = String(message.id || message.message_id || '').trim()

    if (id) {
        const idMatchIndex = mailMessageFieldValueIndex(content, ['ID'], id)

        if (idMatchIndex >= 0) {
            return idMatchIndex
        }
    }

    const subject = String(message.subject || '').trim()

    if (subject) {
        const subjectMatchIndex = mailMessageFieldValueIndex(content, ['Oggetto', 'Subject'], subject)

        if (subjectMatchIndex >= 0) {
            return subjectMatchIndex
        }

        const subjectIndex = content.toLocaleLowerCase().indexOf(subject.toLocaleLowerCase())

        if (subjectIndex >= 0) {
            return subjectIndex
        }
    }

    const uid = String(action?.target?.uid || message.uid || '').trim()

    if (uid) {
        const uidMatchIndex = mailMessageFieldValueIndex(content, ['UID'], uid)

        if (uidMatchIndex >= 0) {
            return uidMatchIndex
        }
    }

    return -1
}

const mailMessageFieldValueIndex = (content, labels, value) => {
    const normalizedValue = String(value || '').trim()

    if (!normalizedValue) {
        return -1
    }

    const labelPattern = labels.map(escapeRegExp).join('|')
    const pattern = new RegExp(`(^|\\n)[ \\t]*(?:[-*+][ \\t]+)?(?:\\*\\*)?(?:${labelPattern})\\s*[:：](?:\\*\\*)?\\s*${escapeRegExp(normalizedValue)}(?:\\b|$)`, 'imu')
    const match = pattern.exec(content)

    return match ? match.index : -1
}

const mailMessageSectionForIndex = (content, sections, matchIndex) => {
    if (!sections.length) {
        return {
            start: 0,
            end: content.length,
        }
    }

    const section = sections.find((item) => (
        item.start <= matchIndex
        && item.end > matchIndex
    ))

    return section || sections[0]
}

const mailMessageSectionInsertionIndex = (content, start, end) => {
    const section = content.slice(start, end)
    const fieldPattern = /^[ \t]*(?:[-*+][ \t]+)?(?:\*\*)?(?:ID|Data|Date|Mittente|From|Oggetto|Subject|Anteprima|Preview)\s*[:：](?:\*\*)?.*$/gmi
    let match
    let lastMatch = null

    while ((match = fieldPattern.exec(section)) !== null) {
        lastMatch = match
    }

    if (!lastMatch) {
        return end
    }

    const fieldEnd = start + lastMatch.index + lastMatch[0].length
    const paragraphEnd = content.indexOf('\n\n', fieldEnd)

    if (paragraphEnd >= 0 && paragraphEnd < end) {
        return paragraphEnd
    }

    return fieldEnd
}

const markMailDraftActionProcessed = (key) => {
    processedMailDraftActionKeys.value = {
        ...processedMailDraftActionKeys.value,
        [key]: true,
    }
}

const markQuoteEntityActionProcessed = (key) => {
    processedQuoteEntityActionKeys.value = {
        ...processedQuoteEntityActionKeys.value,
        [key]: true,
    }
}

const normalizeMailMessageTarget = (value) => {
    if (!value || typeof value !== 'object') {
        return null
    }

    const connectionId = String(value.connection_id || '').trim()
    const folder = String(value.folder || '').trim()
    const uid = String(value.uid || '').trim()

    if (!connectionId || !folder || !uid) {
        return null
    }

    return {
        id: value.id === null || value.id === undefined || value.id === ''
            ? ''
            : String(value.id),
        connection_id: connectionId,
        environment_id: value.environment_id === null || value.environment_id === undefined || value.environment_id === ''
            ? null
            : String(value.environment_id),
        folder,
        uid,
    }
}

const mailMessageTargetKey = (target) => [
    target.connection_id,
    target.environment_id || '',
    target.folder,
    target.uid,
].join('|')

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const collapseExpandedChatWidget = () => {
    if (chatWidgetMode.value === 'expanded') {
        collapseChatWidgetToPanel()
    }
}

const openMailDraft = async (action) => {
    if (!action?.draft) return

    try {
        window.sessionStorage.setItem(MAIL_DRAFT_STORAGE_KEY, JSON.stringify({
            key: action.key,
            draft: action.draft,
            action: action.action,
            created_at: new Date().toISOString(),
        }))
        window.dispatchEvent(new CustomEvent('tamepulse-mail-draft-prepared'))
    } catch (error) {
        localError.value = t('tamepulse.chat.openDraftFailed')
        return
    }

    markMailDraftActionProcessed(action.key)
    collapseExpandedChatWidget()

    try {
        await router.push({
            name: 'tamepulse.mail.index',
            params: { tenantId: route.params.tenantId },
            query: { compose: 'draft' },
        })
    } catch (error) {
        localError.value = t('tamepulse.chat.openDraftFailed')
    }
}

const openMailMessage = async (action) => {
    const target = action?.target

    if (!target) return

    const query = {
        connection_id: target.connection_id,
        folder: target.folder,
        uid: target.uid,
    }

    if (target.environment_id) {
        query.environment_id = target.environment_id
    }

    collapseExpandedChatWidget()

    try {
        await router.push({
            name: 'tamepulse.mail.index',
            params: { tenantId: route.params.tenantId },
            query,
        })
    } catch (error) {
        localError.value = t('tamepulse.chat.openMessageFailed')
    }
}

const openQuoteEntity = async (action, options = {}) => {
    const normalized = normalizeQuoteEntityAction(action)

    if (!normalized) return

    const routeName = normalized.entity === 'person'
        ? (normalized.mode === 'edit' ? 'quote.person.edit' : 'quote.person.create')
        : (normalized.mode === 'edit' ? 'quote.account.edit' : 'quote.account.create')
    const errorKey = normalized.entity === 'person'
        ? 'tamepulse.chat.openPersonFailed'
        : 'tamepulse.chat.openAccountFailed'
    const shouldPrefillForm = normalized.mode === 'create'
        || (normalized.form && Object.keys(normalized.form).length > 0)

    try {
        if (shouldPrefillForm) {
            const payload = {
                key: normalized.key,
                entity: normalized.entity,
                form: normalized.form || {},
                action: normalized,
                created_at: new Date().toISOString(),
            }

            setPendingQuoteEntityForm(payload)
        }

        if (options.markProcessed ?? true) {
            markQuoteEntityActionProcessed(normalized.key)
        }

        collapseExpandedChatWidget()

        await router.push({
            name: routeName,
            params: {
                tenantId: route.params.tenantId,
                ...(normalized.mode === 'edit' ? { id: normalized.id } : {}),
            },
            query: shouldPrefillForm ? { assistant_prefill: normalized.key } : {},
        })
    } catch (error) {
        localError.value = t(errorKey)
    }
}

const currentQuoteEntityRoute = (entity) => {
    if (entity === 'person') {
        if (route.name === 'quote.person.edit') {
            return { mode: 'edit', id: String(route.params.id || '') }
        }

        return { mode: 'create', id: '' }
    }

    if (route.name === 'quote.account.edit') {
        return { mode: 'edit', id: String(route.params.id || '') }
    }

    return { mode: 'create', id: '' }
}

const applyQuoteEntityPatch = async (action) => {
    const normalized = normalizeQuoteEntityPatchAction(action)

    if (!normalized || processedQuoteEntityActionKeys.value[normalized.key]) {
        return
    }

    const applied = applyQuoteEntityFormPatch({
        key: normalized.key,
        entity: normalized.entity,
        form: normalized.form,
        action: normalized,
        created_at: new Date().toISOString(),
    })

    if (applied) {
        markQuoteEntityActionProcessed(normalized.key)
        collapseExpandedChatWidget()
        return
    }

    const fallbackRoute = currentQuoteEntityRoute(normalized.entity)

    await openQuoteEntity({
        key: normalized.key,
        type: 'open_quote_entity',
        entity: normalized.entity,
        mode: fallbackRoute.mode,
        id: fallbackRoute.id,
        presentation: 'auto_open',
        label: normalized.label,
        form: normalized.form,
    }, { markProcessed: false })

    markQuoteEntityActionProcessed(normalized.key)
}

const maybeAutoOpenMailDraft = () => {
    if (!awaitingAutoMailDraftConversationId.value || String(selectedConversationId.value) !== String(awaitingAutoMailDraftConversationId.value)) {
        return
    }

    const messages = assistantMessagesAfterLatestUser(conversationMessages.value)
    const latestCompletedAssistant = [...messages]
        .reverse()
        .find((message) => message.role === 'assistant' && message.status === 'completed')
    const action = mailDraftActions(latestCompletedAssistant)
        .find((item) => item.presentation === 'auto_open' && !processedMailDraftActionKeys.value[item.key])

    if (!action) {
        if (latestCompletedAssistant && !messages.some((message) => message.role === 'assistant' && message.status === 'pending')) {
            awaitingAutoMailDraftConversationId.value = null
        }

        return
    }

    awaitingAutoMailDraftConversationId.value = null
    openMailDraft(action).catch(() => {
        localError.value = t('tamepulse.chat.openDraftFailed')
    })
}

const maybeAutoApplyQuoteEntityPatch = () => {
    const pendingRequest = pendingAutoQuoteEntityPatchRequest.value

    if (!pendingRequest || String(selectedConversationId.value) !== String(pendingRequest.conversationId)) {
        return
    }

    const messages = assistantMessagesAfterLatestUser(conversationMessages.value)
    const latestCompletedAssistant = [...messages]
        .reverse()
        .find((message) => message.role === 'assistant' && message.status === 'completed')
    const actions = quoteEntityPatchActions(latestCompletedAssistant)
    const action = actions
        .find((item) => ['auto_apply', 'auto_open'].includes(item.presentation) && !processedQuoteEntityActionKeys.value[item.key])

    if (!action) {
        return
    }

    pendingAutoQuoteEntityPatchRequest.value = null
    awaitingAutoQuoteEntityConversationId.value = null
    applyQuoteEntityPatch(action).catch(() => {
        localError.value = action.entity === 'person'
            ? t('tamepulse.chat.openPersonFailed')
            : t('tamepulse.chat.openAccountFailed')
    })
}

const maybeAutoOpenQuoteEntity = () => {
    if (!awaitingAutoQuoteEntityConversationId.value || String(selectedConversationId.value) !== String(awaitingAutoQuoteEntityConversationId.value)) {
        return
    }

    const messages = assistantMessagesAfterLatestUser(conversationMessages.value)
    const latestCompletedAssistant = [...messages]
        .reverse()
        .find((message) => message.role === 'assistant' && message.status === 'completed')
    const action = quoteEntityActions(latestCompletedAssistant)
        .find((item) => item.presentation === 'auto_open' && !processedQuoteEntityActionKeys.value[item.key])

    if (!action) {
        return
    }

    awaitingAutoQuoteEntityConversationId.value = null
    openQuoteEntity(action, { markProcessed: true }).catch(() => {
        localError.value = action.entity === 'person'
            ? t('tamepulse.chat.openPersonFailed')
            : t('tamepulse.chat.openAccountFailed')
    })
}

const scrollToBottom = () => {
    nextTick(() => {
        const scroll = () => {
            if (messagesPane.value) {
                messagesPane.value.scrollTop = messagesPane.value.scrollHeight
            }
        }

        scroll()
        window.requestAnimationFrame(() => {
            scroll()
            window.requestAnimationFrame(scroll)
        })
    })
}

const isNearBottom = () => {
    if (!messagesPane.value) return true

    return messagesPane.value.scrollHeight - messagesPane.value.scrollTop - messagesPane.value.clientHeight < 120
}

const formatDate = (value) => {
    if (!value) return '-'

    return new Date(value).toLocaleString()
}

const formatBytes = (value) => {
    const bytes = Number(value ?? 0)

    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
    if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(1)} KB`

    return `${bytes} B`
}

watch(conversations, (items) => {
    if (!selectedConversationId.value && items.length) {
        selectConversation(items[0])
    }
}, { immediate: true })

watch(conversationMessages, () => {
    maybeAutoOpenMailDraft()
    maybeAutoApplyQuoteEntityPatch()
    maybeAutoOpenQuoteEntity()
}, { deep: true })

watch(chatReadOnly, (readOnly) => {
    if (readOnly) {
        chatAllowLocalShell.value = false
    }
})

watch(chatRoutingMode, (mode) => {
    if (mode !== 'profile') {
        selectedChatProfileId.value = ''
    }

    if (mode !== 'agent') {
        selectedChatAgentId.value = ''
    }
})

watch([
    chatRoutingMode,
    selectedChatProfileId,
    selectedChatAgentId,
    chatReadOnly,
    chatAllowLocalShell,
    chatTemperatureOverride,
    chatTemperature,
    chatServiceTier,
], () => {
    scheduleRuntimeSettingsSave()
})

watch(selectedConversationId, () => {
    const conversation = selectedConversation.value

    subscribeToConversation(conversation?.id)

    if (!conversation?.id) {
        loadingInitialMessages.value = false
        return
    }

    if (conversationMessagesById.value[conversationKey(conversation.id)]?.length) {
        loadLatestMessages(conversation.id).catch(() => null)
        scrollToBottom()
        return
    }

    loadingInitialMessages.value = true

    loadLatestMessages(conversation.id, { replace: true })
        .catch((error) => {
            localError.value = error?.graphQLErrors?.[0]?.message || error?.message || t('tamepulse.chat.sendFailed')
        })
        .finally(() => {
            loadingInitialMessages.value = false
        })
}, { immediate: true })

onBeforeUnmount(() => {
    if (runtimeSettingsSaveTimeout) {
        window.clearTimeout(runtimeSettingsSaveTimeout)
        runtimeSettingsSaveTimeout = null
    }

    leaveChatChannel()
})
</script>

<style>
.tamepulse-chat-panel-page {
    height: calc(100vh - 10rem);
    height: calc(100dvh - 10rem);
    max-height: calc(100vh - 10rem);
    max-height: calc(100dvh - 10rem);
    margin-bottom: 1rem;
}

.tamepulse-chat-markdown-preview,
.tamepulse-chat-markdown-preview .md-editor-preview-wrapper,
.tamepulse-chat-markdown-preview .md-editor-preview {
    background: transparent !important;
}

.tamepulse-chat-markdown-preview .md-editor-preview-wrapper,
.tamepulse-chat-markdown-preview .md-editor-preview {
    padding: 0 !important;
}

.tamepulse-chat-markdown-preview .md-editor-preview {
    color: #334155;
    font-size: 0.875rem;
    line-height: 1.65;
}

.tamepulse-chat-markdown-preview .md-editor-preview h1,
.tamepulse-chat-markdown-preview .md-editor-preview h2,
.tamepulse-chat-markdown-preview .md-editor-preview h3 {
    margin: 0.5rem 0;
    color: #0f172a;
    line-height: 1.25;
}

.tamepulse-chat-markdown-preview .md-editor-preview h1 {
    font-size: 1.25rem;
}

.tamepulse-chat-markdown-preview .md-editor-preview h2 {
    font-size: 1.125rem;
}

.tamepulse-chat-markdown-preview .md-editor-preview h3 {
    font-size: 1rem;
}

.tamepulse-chat-markdown-preview .md-editor-preview p,
.tamepulse-chat-markdown-preview .md-editor-preview ul,
.tamepulse-chat-markdown-preview .md-editor-preview ol,
.tamepulse-chat-markdown-preview .md-editor-preview pre,
.tamepulse-chat-markdown-preview .md-editor-preview blockquote {
    margin: 0.5rem 0;
}

.tamepulse-chat-markdown-preview .md-editor-preview p:first-child,
.tamepulse-chat-markdown-preview .md-editor-preview ul:first-child,
.tamepulse-chat-markdown-preview .md-editor-preview ol:first-child,
.tamepulse-chat-markdown-preview .md-editor-preview pre:first-child,
.tamepulse-chat-markdown-preview .md-editor-preview blockquote:first-child {
    margin-top: 0;
}

.tamepulse-chat-markdown-preview .md-editor-preview p:last-child,
.tamepulse-chat-markdown-preview .md-editor-preview ul:last-child,
.tamepulse-chat-markdown-preview .md-editor-preview ol:last-child,
.tamepulse-chat-markdown-preview .md-editor-preview pre:last-child,
.tamepulse-chat-markdown-preview .md-editor-preview blockquote:last-child {
    margin-bottom: 0;
}

.tamepulse-chat-markdown-preview .md-editor-preview table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
}

.tamepulse-chat-markdown-preview .md-editor-preview th,
.tamepulse-chat-markdown-preview .md-editor-preview td {
    white-space: nowrap;
    word-break: normal;
}

.tamepulse-chat-markdown-preview .md-editor-preview td {
    vertical-align: top;
}

.tamepulse-chat-scroll {
    scrollbar-color: rgb(148 163 184 / 0.55) transparent;
    scrollbar-width: thin;
}

.tamepulse-chat-scroll::-webkit-scrollbar {
    width: 0.375rem;
}

.tamepulse-chat-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.tamepulse-chat-scroll::-webkit-scrollbar-thumb {
    border-radius: 9999px;
    background: rgb(148 163 184 / 0.35);
}

.tamepulse-chat-scroll::-webkit-scrollbar-thumb:hover {
    background: rgb(100 116 139 / 0.45);
}
</style>
